import { DEFAULT_PROMOTION_COLOR, DEFAULT_WRAPPER_TEMPLATE, DEFAULT_CARD_TEMPLATE } from "./constants";
import { Promotion, Item, PromotionStatus, ItemType, BillingFrequency, PromotionUserInfo, PromotionColor } from "./models";
import { fetchPromotions, verifyCoupon, requestTransactionIntent, submitSignup, sendContactEmail, fetchPublishedTemplate, fetchSplitTest, recordSplitTestView, recordPageView, SplitTestResponse, SplitTestVariant } from "./api";

interface CouponCode {
  code: string;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  min_amount?: number;
  max_discount?: number;
  promotion_id: string;
  is_valid: boolean;
  currency?: string;
}

interface CouponValidation {
  isValid: boolean;
  message: string;
  isLoading: boolean;
}

interface UserInfoField {
  id: string;
  label: string;
  type: string;
  placeholder: string;
  required: boolean;
  defaultValue: string;
}

const COUPON_APPLY_DELAY_MS = 600;
const PREVIEW_MESSAGE_DURATION_MS = 2600;
const DEFAULT_TEXT_COLOR = DEFAULT_PROMOTION_COLOR.font_color;

const FEATURE_FLAGS = {
  enableBillingToggle: false
} as const;

const ENABLE_COUPON_SECTION = false;

// ---- Money utils (integer minor-units) ----

const CURRENCY_EXPONENT: Record<string, number> = {
  KRW: 0, JPY: 0, VND: 0,
  USD: 2, EUR: 2, GBP: 2, CAD: 2, AUD: 2, CHF: 2, CNY: 2,
  BHD: 3, JOD: 3, KWD: 3, OMR: 3, TND: 3,
};

const expOf = (c: string) => CURRENCY_EXPONENT[c?.toUpperCase()] ?? 2;
const pow10 = (n: number) => (n >= 0 ? Math.pow(10, n) : 1);

/** major → minor (e.g., USD 200.34 → 20034) */
const toMinorStrict = (currency: string, major: number): number => {
  const e = expOf(currency);
  return Math.max(0, Math.round(major * pow10(e)));
};

/** minor → major (e.g., 20034 → 200.34) */
const fromMinor = (currency: string, minor: number): number => {
  const e = expOf(currency);
  return minor / pow10(e);
};

const formatPriceFromMinor = (currency: string, minor: number): string => {
  // Return $0.00 if minor is invalid, NaN, null/undefined, or zero
  if (minor == null || isNaN(minor) || !isFinite(minor) || minor === 0) {
    return '$0.00';
  }
  
  const majorAmount = fromMinor(currency, minor);
  
  // Return $0.00 if majorAmount is invalid, NaN, or not finite
  if (isNaN(majorAmount) || !isFinite(majorAmount) || majorAmount === 0) {
    return '$0.00';
  }
  
  const exp = expOf(currency);

  const hasDecimals = exp > 0 && (majorAmount % 1 !== 0);

  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency?.toUpperCase() || 'USD',
    minimumFractionDigits: hasDecimals ? exp : 0,
    maximumFractionDigits: exp,
  }).format(majorAmount);

  return formatted;
};

// ---- Split test utility functions ----

/**
 * Generate or retrieve a visitor ID from localStorage
 */
function getOrCreateVisitorId(): string {
  if (typeof window === 'undefined') return '';

  const STORAGE_KEY = 'klaim_visitor_id';
  let visitorId = localStorage.getItem(STORAGE_KEY);

  if (!visitorId) {
    // Generate a simple unique ID: timestamp + random string
    visitorId = `v_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    localStorage.setItem(STORAGE_KEY, visitorId);
  }

  return visitorId;
}

/**
 * Get or set the split test variant cookie
 */
function getSplitTestCookie(testId: string): string | null {
  if (typeof document === 'undefined') return null;

  const cookieName = `klaim_st_${testId}`;
  const cookies = document.cookie.split(';');

  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === cookieName) {
      return decodeURIComponent(value);
    }
  }

  return null;
}

function setSplitTestCookie(testId: string, variantId: string): void {
  if (typeof document === 'undefined') return;

  const cookieName = `klaim_st_${testId}`;
  const expiryDays = 30;
  const expiryDate = new Date();
  expiryDate.setDate(expiryDate.getDate() + expiryDays);

  // Set cookie with SameSite=None and Secure for cross-domain support
  document.cookie = `${cookieName}=${encodeURIComponent(variantId)}; expires=${expiryDate.toUTCString()}; path=/; SameSite=None; Secure`;
}

/**
 * Assign a variant based on distribution percentages
 */
function assignVariant(variants: SplitTestVariant[]): SplitTestVariant {
  // Sort by sort_order to ensure consistent ordering
  const sortedVariants = [...variants].sort((a, b) => a.sort_order - b.sort_order);

  // Generate random number between 0 and 100
  const random = Math.random() * 100;

  // Build cumulative distribution and find matching variant
  let cumulative = 0;
  for (const variant of sortedVariants) {
    cumulative += variant.distribution_percent;
    if (random < cumulative) {
      return variant;
    }
  }

  // Fallback to first variant (should not happen if percentages sum to 100)
  return sortedVariants[0];
}

/**
 * Check if we've already recorded a view today for this visitor+test
 */
function hasRecordedViewToday(testId: string, visitorId: string): boolean {
  if (typeof localStorage === 'undefined') return false;

  const storageKey = `klaim_st_view_${testId}_${visitorId}`;
  const lastViewDate = localStorage.getItem(storageKey);

  if (!lastViewDate) return false;

  const today = new Date().toDateString();
  return lastViewDate === today;
}

/**
 * Mark that we've recorded a view today
 */
function markViewRecorded(testId: string, visitorId: string): void {
  if (typeof localStorage === 'undefined') return;

  const storageKey = `klaim_st_view_${testId}_${visitorId}`;
  const today = new Date().toDateString();
  localStorage.setItem(storageKey, today);
}

class KlaimPricingTableV2 extends HTMLElement {
  private promotion: Promotion | null = null;
  private promotionColor: PromotionColor;
  private userInfoFields: UserInfoField[];

  private selectedItem: Item | null;
  private formValues: Record<string, string>;
  private fieldErrors: Record<string, string> = {};
  private couponCode = '';
  private appliedCoupon: CouponCode | null = null;

  // Split test context
  private splitTestId: string | null = null;
  private splitTestVariantId: string | null = null;
  private pageViewFired = false;
  private couponValidation: CouponValidation = { isValid: false, message: '', isLoading: false };
  private coupons: CouponCode[];
  private isSubmitting = false;
  private paymentError: string | null = null;
  private isLoading = false;
  private loadError: string | null = null;
  private billingPeriod: 'monthly' | 'annual' = 'monthly';
  private pendingBillingAnimation = false;
  private viewportMode: 'desktop' | 'mobile' = 'desktop';
  private previewMessageVisible = false;
  private previewMessageTimeout: number | null = null;
  private forcedPreviewMode = false;
  private activeSubmitButton: HTMLButtonElement | null = null;
  private activeSubmitButtonContent: string | null = null;
  private activeCardElement: HTMLElement | null = null;
  private isContactModalOpen = false;
  private contactModalJustOpened = false;
  private contactModalClosing = false;
  private isUnclaimedModalOpen = false;
  private unclaimedModalJustOpened = false;
  private unclaimedModalClosing = false;
  private isDraftModalOpen = false;
  private draftModalJustOpened = false;
  private draftModalClosing = false;
  private contactFormData: {
    name: string;
    email: string;
    message: string;
  } = {
    name: '',
    email: '',
    message: ''
  };
  private contactSubmitState: 'idle' | 'loading' | 'success' = 'idle';

  private readonly handleClick: (event: Event) => void;
  private readonly handleInput: (event: Event) => void;
  private readonly handleKeyDown: (event: Event) => void;
  private readonly handleSubmit: (event: Event) => void;

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });

    // Initialize with defaults (updated after the promotion loads)
    this.promotionColor = this.resolvePromotionColor(undefined);
    this.userInfoFields = [];
    this.formValues = {};
    this.fieldErrors = {};
    this.selectedItem = null;
    this.coupons = [];

    this.handleClick = this.onShadowClick.bind(this);
    this.handleInput = this.onShadowInput.bind(this);
    this.handleKeyDown = this.onShadowKeyDown.bind(this);
    this.handleSubmit = this.onFormSubmit.bind(this);
  }

  async connectedCallback(): Promise<void> {
    // Initialize formValues as empty object first
    if (!this.formValues || Object.keys(this.formValues).length === 0) {
      this.formValues = {};
    }

    // Read user info from attributes (if already set)
    const userEmail = this.getAttribute('user-email');
    const userName = this.getAttribute('user-name');

    // Initialize formValues with user info from attributes
    if (userEmail) {
      this.formValues.email = userEmail;
    }
    if (userName) {
      this.formValues.name = userName;
    }

    // Check for split-test-id attribute first
    const splitTestId = this.getAttribute('split-test-id');

    if (splitTestId) {
      // Handle split test mode
      await this.handleSplitTest(splitTestId);
      this.render();
      this.bindEvents();
      return;
    }

    // Parse entity_id and page_id from the pricing-table-id attribute
    const pricingTableId = this.getAttribute('pricing-table-id');

    if (!pricingTableId) {
      this.loadError = 'Either pricing-table-id or split-test-id attribute is required.';
      this.render();
      this.bindEvents();
      return;
    }

    // Expected format: "entityID/pageID" or "entityID/subpath/pageID" (split on the last '/')
    const lastSlashIndex = pricingTableId.lastIndexOf('/');
    if (lastSlashIndex === -1 || lastSlashIndex === 0 || lastSlashIndex === pricingTableId.length - 1) {
      this.loadError = 'Invalid pricing-table-id format. (e.g., "entityID/pageID" or "entityID/subpath/pageID")';
      this.render();
      this.bindEvents();
      return;
    }

    const entity_id = pricingTableId.substring(0, lastSlashIndex);
    const page_id = pricingTableId.substring(lastSlashIndex + 1);

    // Load promotion
    await this.loadPromotion(entity_id, page_id);

    this.render();
    this.bindEvents();
  }

  private async loadPromotion(entity_id: string, page_id: string): Promise<void> {
    this.isLoading = true;
    this.loadError = null;
    this.dispatchCustomEvent('klaim:load-start', {
      entity_id,
      page_id
    });
    this.render(); // Display loading state

    try {
      let promotion = await fetchPromotions(entity_id, page_id);

      // Handle split test pages: assign variant client-side, then re-fetch
      const splitTest = promotion?._split_test;
      if (splitTest?.variants) {
        const testId = splitTest.id;
        const variants: SplitTestVariant[] = splitTest.variants;

        const cookieVariantId = getSplitTestCookie(testId);
        let selectedVariant: SplitTestVariant | undefined;

        if (cookieVariantId) {
          selectedVariant = variants.find((v: SplitTestVariant) => v.id === cookieVariantId);
        }

        if (!selectedVariant) {
          if (splitTest.status === 'RUNNING') {
            selectedVariant = assignVariant(variants);
          } else if (splitTest.status === 'STOPPED') {
            selectedVariant = [...variants].sort((a: SplitTestVariant, b: SplitTestVariant) => a.sort_order - b.sort_order)[0];
          } else {
            this.loadError = 'This split test is not currently running';
            this.dispatchCustomEvent('klaim:error', { type: 'load', message: this.loadError });
            return;
          }
          if (selectedVariant) {
            setSplitTestCookie(testId, selectedVariant.id);
          }
        }

        if (!selectedVariant) {
          this.loadError = 'Could not determine variant';
          this.dispatchCustomEvent('klaim:error', { type: 'load', message: this.loadError });
          return;
        }

        // Record view (deduplicated)
        const visitorId = getOrCreateVisitorId();
        if (!hasRecordedViewToday(testId, visitorId)) {
          recordSplitTestView(testId, selectedVariant.id, visitorId).catch(() => {});
          markViewRecorded(testId, visitorId);
        }

        // Store split test context for transaction tracking
        this.splitTestId = testId;
        this.splitTestVariantId = selectedVariant.id;

        // Re-fetch with variant_id to get the actual pricing page
        promotion = await fetchPromotions(entity_id, page_id, selectedVariant.id);

        this.dispatchCustomEvent('klaim:split-test-assigned', {
          split_test_id: testId,
          variant_id: selectedVariant.id,
        });
      } else if (splitTest?.id && splitTest?.variant_id) {
        // Already resolved (variant_id was in the initial request)
        this.splitTestId = splitTest.id;
        this.splitTestVariantId = splitTest.variant_id;
      }

      // Load template for the resolved page
      const resolvedPageId = promotion?.page_id || page_id;
      const templateData = await fetchPublishedTemplate(resolvedPageId).catch(() => null);

      this.promotion = promotion;

      // Fire a direct (non-split-test) pageview once per widget instance.
      // Split-test loads are recorded separately via recordSplitTestView (the
      // server routes that to the same page_views table). Server-side dedup
      // makes repeat fires within a UTC day a no-op.
      this.firePageViewIfDirect();

      // Initialize with promotion data
      if (this.promotion) {
        if (templateData?.template_data) {
          this.promotion.custom_template = templateData.template_data;
        }
        this.promotionColor = this.resolvePromotionColor(this.promotion.color);
        this.userInfoFields = [];
        if (!this.formValues || Object.keys(this.formValues).length === 0) {
          this.formValues = {};
        }
        this.fieldErrors = {};

        const initialItem = this.promotion.items?.[0] ?? null;
        this.selectedItem = initialItem;

        if (initialItem) {
          this.dispatchCustomEvent('klaim:item-selected', {
            item: initialItem,
            promotion_id: this.promotion.promotion_id
          });
        }
      }

      this.coupons = [];

      this.dispatchCustomEvent('klaim:load-end', {
        success: true,
        promotion: this.promotion,
        entity_id,
        page_id
      });
    } catch (error) {
      console.error('Failed to load promotion:', error);
      this.loadError = error instanceof Error
        ? error.message
        : 'An error occurred while loading the promotion.';
      this.promotion = null;

      this.dispatchCustomEvent('klaim:error', {
        type: 'load',
        message: this.loadError,
        entity_id,
        page_id,
        error: error instanceof Error ? error.message : String(error)
      });

      this.dispatchCustomEvent('klaim:load-end', {
        success: false,
        error: this.loadError,
        entity_id,
        page_id
      });
    } finally {
      this.isLoading = false;
    }
  }

  private firePageViewIfDirect(): void {
    if (this.pageViewFired) return;
    if (this.splitTestId) return; // split test path records its own view
    // klaim-embed's api.ts maps backend `id` (UUID) into `promotion_id`
    const pricingPageId = this.promotion?.promotion_id;
    if (!pricingPageId) return;

    this.pageViewFired = true;
    const visitorId = getOrCreateVisitorId();
    let referrer: string | undefined;
    let utmSource: string | undefined;
    let utmMedium: string | undefined;
    let utmCampaign: string | undefined;
    if (typeof document !== 'undefined') {
      referrer = document.referrer || undefined;
    }
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      utmSource = params.get('utm_source') || undefined;
      utmMedium = params.get('utm_medium') || undefined;
      utmCampaign = params.get('utm_campaign') || undefined;
    }
    recordPageView({
      pricing_page_id: pricingPageId,
      visitor_id: visitorId,
      source: 'embed',
      referrer,
      utm_source: utmSource,
      utm_medium: utmMedium,
      utm_campaign: utmCampaign,
    });
  }

  private async handleSplitTest(testId: string): Promise<void> {
    this.isLoading = true;
    this.loadError = null;
    this.dispatchCustomEvent('klaim:load-start', {
      split_test_id: testId
    });
    this.render(); // Display loading state

    try {
      // Fetch split test data
      const splitTest: SplitTestResponse = await fetchSplitTest(testId);

      if (!splitTest.variants || splitTest.variants.length === 0) {
        this.loadError = 'Split test has no variants';
        this.dispatchCustomEvent('klaim:error', {
          type: 'load',
          message: this.loadError,
          split_test_id: testId
        });
        return;
      }

      // Get or create visitor ID
      const visitorId = getOrCreateVisitorId();

      // Check for existing cookie
      const cookieVariantId = getSplitTestCookie(testId);

      let selectedVariant: SplitTestVariant | undefined;

      if (cookieVariantId) {
        // Find the variant from the cookie
        selectedVariant = splitTest.variants.find(v => v.id === cookieVariantId);
      }

      // If no cookie or invalid variant ID, assign a new variant
      if (!selectedVariant) {
        if (splitTest.status === 'RUNNING') {
          // Assign based on distribution
          selectedVariant = assignVariant(splitTest.variants);
        } else if (splitTest.status === 'STOPPED') {
          // For stopped tests: use first variant for new visitors
          selectedVariant = [...splitTest.variants].sort((a, b) => a.sort_order - b.sort_order)[0];
        } else {
          // DRAFT status - shouldn't be publicly accessible
          this.loadError = 'This split test is not currently running';
          this.dispatchCustomEvent('klaim:error', {
            type: 'load',
            message: this.loadError,
            split_test_id: testId
          });
          return;
        }

        // Set the cookie for future visits
        if (selectedVariant) {
          setSplitTestCookie(testId, selectedVariant.id);
        }
      }

      if (!selectedVariant) {
        this.loadError = 'Could not determine variant';
        this.dispatchCustomEvent('klaim:error', {
          type: 'load',
          message: this.loadError,
          split_test_id: testId
        });
        return;
      }

      // Record view event (deduplicated per visitor per day)
      if (!hasRecordedViewToday(testId, visitorId)) {
        await recordSplitTestView(testId, selectedVariant.id, visitorId);
        markViewRecorded(testId, visitorId);
      }

      // Store split test context for transaction tracking
      this.splitTestId = testId;
      this.splitTestVariantId = selectedVariant.id;

      // Get the pricing page details from the variant
      const pricingPage = selectedVariant.pricing_page;
      if (!pricingPage) {
        this.loadError = 'Variant has no pricing page';
        this.dispatchCustomEvent('klaim:error', {
          type: 'load',
          message: this.loadError,
          split_test_id: testId
        });
        return;
      }

      // Load the pricing page from the assigned variant
      const { entity_id, page_id } = pricingPage;
      await this.loadPromotion(entity_id, page_id);

      this.dispatchCustomEvent('klaim:split-test-assigned', {
        split_test_id: testId,
        variant_id: selectedVariant.id,
        pricing_page_id: page_id,
        entity_id
      });

    } catch (error) {
      console.error('Failed to handle split test:', error);
      this.loadError = error instanceof Error
        ? error.message
        : 'An error occurred while loading the split test.';

      this.dispatchCustomEvent('klaim:error', {
        type: 'load',
        message: this.loadError,
        split_test_id: testId,
        error: error instanceof Error ? error.message : String(error)
      });

      this.dispatchCustomEvent('klaim:load-end', {
        success: false,
        error: this.loadError,
        split_test_id: testId
      });
    } finally {
      this.isLoading = false;
    }
  }

  disconnectedCallback(): void {
    this.unbindEvents();
    this.clearPreviewMessageTimer();
    this.previewMessageVisible = false;
    this.resetActiveButtonState();
  }

  static get observedAttributes(): string[] {
    return ['user-email', 'user-name', 'viewport-mode', 'preview-mode'];
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (name === 'preview-mode') {
      const isPreview = newValue === null
        ? false
        : (() => {
            const nextValue = newValue.toLowerCase();
            if (nextValue === '' || nextValue === 'true' || nextValue === '1') return true;
            if (nextValue === 'false' || nextValue === '0') return false;
            return true;
          })();
      if (this.forcedPreviewMode !== isPreview) {
        this.forcedPreviewMode = isPreview;
        if (this.isConnected) {
          this.render();
        }
      }
      return;
    }

    if (name === 'viewport-mode') {
      const nextMode: 'desktop' | 'mobile' = newValue === 'mobile' ? 'mobile' : 'desktop';
      if (nextMode !== this.viewportMode) {
        this.viewportMode = nextMode;
        if (this.isConnected) {
          this.render();
        }
      }
      return;
    }

    if (name === 'user-email' || name === 'user-name') {
      const fieldName = name === 'user-email' ? 'email' : 'name';
      if (newValue !== null && newValue !== oldValue) {
        // Ensure formValues is initialized
        if (!this.formValues) {
          this.formValues = {};
        }
        this.formValues[fieldName] = newValue;
        // Clear any field errors when value changes
        if (this.fieldErrors[fieldName]) {
          delete this.fieldErrors[fieldName];
        }
        // Re-render to reflect the changes
        this.render();
      }
    }
  }

  // Public API for external control
  public setSubmitting(value: boolean): void {
    this.isSubmitting = value;
    this.render();
  }

  public setPaymentError(error: string | null): void {
    this.paymentError = error;
    this.render();
  }

  public getSelectedItem(): Item | null {
    return this.selectedItem;
  }

  public getFormData(): Record<string, string> {
    return { ...this.formValues };
  }

  public setFormValues(values: Record<string, string>): void {
    if (!this.formValues) {
      this.formValues = {};
    }
    Object.assign(this.formValues, values);
    // Clear errors for updated fields
    Object.keys(values).forEach(key => {
      if (this.fieldErrors[key]) {
        delete this.fieldErrors[key];
      }
    });
    this.render();
  }

  public getAppliedCoupon(): CouponCode | null {
    return this.appliedCoupon;
  }

  private bindEvents(): void {
    if (!this.shadowRoot) {
      return;
    }

    this.shadowRoot.addEventListener('click', this.handleClick);
    this.shadowRoot.addEventListener('input', this.handleInput);
    this.shadowRoot.addEventListener('change', this.handleInput);
    this.shadowRoot.addEventListener('keydown', this.handleKeyDown); // Handle Enter/Esc shortcuts
    this.shadowRoot.addEventListener('submit', this.handleSubmit);
  }

  private unbindEvents(): void {
    if (!this.shadowRoot) {
      return;
    }

    this.shadowRoot.removeEventListener('click', this.handleClick);
    this.shadowRoot.removeEventListener('input', this.handleInput);
    this.shadowRoot.removeEventListener('change', this.handleInput);
    this.shadowRoot.removeEventListener('keydown', this.handleKeyDown);
    this.shadowRoot.removeEventListener('submit', this.handleSubmit);
  }

  private computeUserInfoFields(userInfo?: PromotionUserInfo): UserInfoField[] {
    const hasCustomUserInfo = userInfo && Object.keys(userInfo).length > 0;
    const baseInfo: PromotionUserInfo = hasCustomUserInfo
      ? userInfo!
      : { name: 'string', email: 'string' };

    return Object.keys(baseInfo).map((key) => {
      const fieldType = baseInfo[key] ?? 'string';
      const defaultValue = '';
      const label = this.toTitleCase(key);
      
      // Determine the HTML input type based on fieldType and name
      let htmlInputType = 'text';
      if (fieldType === 'number') {
        htmlInputType = 'number';
      } else if (key.toLowerCase() === 'email') {
        htmlInputType = 'email';
      }
      
      return {
        id: key,
        label,
        type: htmlInputType,
        placeholder: `Enter your ${label.toLowerCase()}`,
        required: true,
        defaultValue
      };
    });
  }

  private initializeFormValues(fields: UserInfoField[]): Record<string, string> {
    return fields.reduce<Record<string, string>>((acc, field) => {
      acc[field.id] = field.defaultValue;
      return acc;
    }, {});
  }

  private resolvePromotionColor(colorSet?: PromotionColor): PromotionColor {
    const fallback = DEFAULT_PROMOTION_COLOR;
    return {
      primary: colorSet?.primary || fallback.primary,
      secondary: colorSet?.secondary || fallback.secondary,
      font_color: colorSet?.font_color || fallback.font_color,
      font_color_secondary: colorSet?.font_color_secondary || fallback.font_color_secondary,
      background_color: colorSet?.background_color || fallback.background_color,
    };
  }

  private toTitleCase(value: string): string {
    const spaced = value.replace(/[_-]+/g, ' ');
    return spaced
      .split(' ')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  private onShadowClick(event: Event): void {
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }

    const toggleButton = target.closest('[data-period]');
    if (toggleButton instanceof HTMLElement) {
      if (!FEATURE_FLAGS.enableBillingToggle) {
        event.preventDefault();
        return;
      }
      const period = toggleButton.dataset.period;
      if (period === 'monthly' || period === 'annual') {
        if (this.billingPeriod !== period) {
          this.billingPeriod = period;
          this.pendingBillingAnimation = true;
          this.render();
        }
      }
      return;
    }

    const applyButton = target.closest('[data-action="apply-coupon"]');
    if (applyButton instanceof HTMLElement) {
      event.preventDefault();
      this.handleApplyCoupon();
      return;
    }

    const removeButton = target.closest('[data-action="remove-coupon"]');
    if (removeButton instanceof HTMLElement) {
      event.preventDefault();
      this.handleRemoveCoupon();
      return;
    }

    const payNowButton = target.closest('[data-action="pay-now"]');
    if (payNowButton) {
      event.preventDefault();
      event.stopPropagation(); // Prevent card selection event
      const activeCard = payNowButton.closest<HTMLElement>('.pricing-card');
      this.setActiveCardHighlight(activeCard || null);
      
      if (this.isUnclaimed) {
        this.clearActiveCardHighlight();
        this.showUnclaimedModal();
        return;
      }

      if (this.isDraftMode) {
        this.clearActiveCardHighlight();
        this.showDraftModal();
        return;
      }

      // Get item ID from button or find parent card
      const itemId = payNowButton.getAttribute('data-item-id') || 
                    payNowButton.closest('[data-item-id]')?.getAttribute('data-item-id');
      
      if (itemId) {
        // Select the item first (suppress immediate re-render to keep CTA animation smooth)
        this.setSelectedItem(itemId, { suppressRender: true });

        if (this.isPreviewMode) {
          this.showPreviewBlockMessage();
          return;
        }
      }

      if (this.isPreviewMode) {
        this.showPreviewBlockMessage();
        return;
      }

      if (this.isSubmitting) {
        this.clearActiveCardHighlight();
        return;
      }

      if (!itemId) {
        this.clearActiveCardHighlight();
        return;
      }
      // Form validation before setting loading state
      if (!this.validateForm()) {
        this.clearActiveCardHighlight();
        return;
      }

      this.isSubmitting = true;
      const buttonElement = payNowButton instanceof HTMLButtonElement
        ? payNowButton
        : payNowButton.querySelector('button');
      if (buttonElement instanceof HTMLButtonElement) {
        this.setActiveButtonLoadingState(buttonElement);
      }

      // Directly call the submit logic instead of using form requestSubmit
      // (render() replaces innerHTML, so form element might not be available)
      this.processPaymentOrSignup();

      return;
    }

    const itemCard = target.closest<HTMLElement>('[data-item-id]');
    if (itemCard && !itemCard.classList.contains('pricing-card--disabled')) {
      if (this.isUnclaimed) {
        this.showUnclaimedModal();
        return;
      }
      if (this.isDraftMode) {
        this.showDraftModal();
        return;
      }
      if (this.isSubmitting) {
        return;
      }
      const itemId = itemCard.dataset.itemId;
      if (itemId) {
        this.setSelectedItem(itemId);
        // setSelectedItem already dispatches events internally
      }
      return;
    }
  }

  private onShadowKeyDown(event: Event): void {
    if (!(event instanceof KeyboardEvent)) {
      return;
    }

    const targetElement = event.target as HTMLElement | null;
    if (!targetElement) {
      return;
    }

    if (targetElement instanceof HTMLInputElement && targetElement.name === 'couponCode') {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.handleApplyCoupon();
        return;
      }

      if (event.key === 'Escape') {
        if (this.appliedCoupon) {
          this.couponCode = this.appliedCoupon.code;
          targetElement.value = this.couponCode;
        } else {
          this.couponCode = '';
          targetElement.value = '';
        }
        this.couponValidation = { isValid: !!this.appliedCoupon, message: '', isLoading: false };
        this.updateCouponButtonState();
        return;
      }
      return;
    }

    const itemCard = targetElement.closest<HTMLElement>('[data-item-id]');
    if (!itemCard) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (this.isDraftMode) {
        this.showDraftModal();
        return;
      }
      if (this.isSubmitting) {
        return;
      }
      const itemId = itemCard.dataset.itemId;
      if (itemId) {
        this.setSelectedItem(itemId);
      }
      return;
    }
  }

  private onShadowInput(event: Event): void {
    const target = event.target as HTMLInputElement | HTMLSelectElement | null;
    if (!target) {
      return;
    }

    const { name, value } = target;

    if (name === 'couponCode') {
      this.couponCode = value;

      // ✅ Reset coupon state and submit button when the input is empty
      if (!value.trim()) {
        this.appliedCoupon = null;
        this.couponValidation = { isValid: false, message: '', isLoading: false };
        this.render();   // Update button label/disabled state immediately
        return;
      }

      // Typing alone should not clear an applied coupon
      this.couponValidation = { ...this.couponValidation, message: '' };
      this.updateCouponButtonState();
      return;
    }

    if (this.formValues[name] !== undefined) {
      this.formValues[name] = value;
      
      // Clear field errors while the user types
      if (this.fieldErrors[name]) {
        delete this.fieldErrors[name];
        this.clearFieldError(name);
      }
    }
  }

  private clearCouponMessages(): void {
    if (!this.shadowRoot) return;
    
    // Remove coupon message
    const couponMessage = this.shadowRoot.querySelector('.coupon-message');
    if (couponMessage) {
      couponMessage.remove();
    }
    
    // Remove applied coupon details
    const appliedCoupon = this.shadowRoot.querySelector('.applied-coupon');
    if (appliedCoupon) {
      appliedCoupon.remove();
    }
  }

  private clearFieldError(fieldId: string): void {
    if (!this.shadowRoot) return;
    
    const inputField = this.shadowRoot.querySelector(`#${fieldId}`)?.closest('.input-field');
    if (inputField) {
      const errorElement = inputField.querySelector('.input-field__error');
      if (errorElement) {
        errorElement.remove();
      }
      const input = inputField.querySelector('input');
      if (input) {
        input.classList.remove('input-field__input--error');
      }
    }
  }

  private validateForm(): boolean {
    this.fieldErrors = {};
    let isValid = true;

    // Validate email and name from formValues
    const email = (this.formValues.email || '').trim();
    const name = (this.formValues.name || '').trim();

    // Validate email
    if (!email) {
      this.fieldErrors.email = 'Email is required.';
      isValid = false;
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        this.fieldErrors.email = 'Please enter a valid email address.';
        isValid = false;
      }
    }

    // Validate name
    if (!name) {
      this.fieldErrors.name = 'Name is required.';
      isValid = false;
    }

    if (!isValid) {
      // Call render after updating fieldErrors so render can read them
      this.render();
    }

    return isValid;
  }

  private updateCouponButtonState(): void {
    if (!this.shadowRoot) return;
    
    const applyButton = this.shadowRoot.querySelector<HTMLButtonElement>('[data-action="apply-coupon"]');
    if (applyButton) {
      const isDisabled = this.isSubmitting || this.couponValidation.isLoading || !this.couponCode.trim();
      applyButton.disabled = isDisabled;
    }
  }

  private async onFormSubmit(event: Event): Promise<void> {
    event.preventDefault();
    const target = event.target as HTMLElement | null;
    if (target?.hasAttribute('data-contact-form')) {
      return;
    }
    this.processPaymentOrSignup();
  }

  private async processPaymentOrSignup(): Promise<void> {
    if (!this.promotion) {
      this.isSubmitting = false;
      this.resetActiveButtonState();
      this.render();
      return;
    }

    if (this.isPreviewMode) {
      this.isSubmitting = false;
      this.resetActiveButtonState();
      this.showPreviewBlockMessage();
      return;
    }
    
    // Guard draft mode
    if (this.isDraftMode) {
      this.isSubmitting = false;
      this.resetActiveButtonState();
      this.showDraftModal();
      return;
    }

    if (!this.selectedItem) {
      this.isSubmitting = false;
      this.resetActiveButtonState();
      this.render();
      return;
    }

    // Block all actions when pricing page is unclaimed (no app_id)
    if (this.isUnclaimed) {
      this.isSubmitting = false;
      this.resetActiveButtonState();
      this.showUnclaimedModal();
      return;
    }

    // Handle Contact type items - open contact modal
    if (this.selectedItem && this.isContactItem(this.selectedItem)) {
      const activeItemId = this.selectedItem.item_id;
      this.isSubmitting = false;
      this.resetActiveButtonState({ preserveActiveCard: true });
      
      // Pre-fill email and name if available from form values
      this.contactFormData.email = this.formValues.email || '';
      this.contactFormData.name = this.formValues.name || '';
      
      // Open contact modal (keep card highlight active)
      this.isContactModalOpen = true;
      this.contactModalJustOpened = true;
      this.contactModalClosing = false;
      this.contactSubmitState = 'idle';
      this.render();
      // Restore active card highlight after render to maintain animation
      this.restoreActiveCardHighlight(activeItemId);
      // Reset animation flag after a short delay
      setTimeout(() => {
        this.contactModalJustOpened = false;
      }, 350);
      
      this.dispatchCustomEvent('klaim:contact', {
        item: this.selectedItem,
        formData: this.formValues,
        promotion_id: this.promotion.promotion_id
      });
      return;
    }

    // Form validation (already done in onShadowClick, but double-check)
    if (!this.validateForm()) {
      this.isSubmitting = false;
      this.resetActiveButtonState();
      return;
    }

    // Get success_url and cancel_url from component attributes
    const successUrl = this.getAttribute('success-url');
    const cancelUrl = this.getAttribute('cancel-url');

    // Calculate the final amount using integer minor units
    const currency = (
      (this.selectedItem as any)?.currency?.toUpperCase()
      || this.appliedCoupon?.currency?.toUpperCase()
      || 'USD'
    );
    const finalPriceMinor = this.calculateFinalPriceMinor(this.selectedItem, this.appliedCoupon);
    const finalPrice = fromMinor(currency, finalPriceMinor);
    
    // Get user info from form values
    const name = this.formValues.name || '';
    const email = this.formValues.email || '';

    // Handle payment required vs free/zero-cost cases
    // Note: Contact type is already handled above, so we only check for Free here
    const requiresPayment = finalPriceMinor > 0 && this.selectedItem.type !== ItemType.Free;

    if (requiresPayment) {
      // === COMMENTED OUT: Original Stripe Checkout redirect flow ===
      // if (!successUrl || !cancelUrl) {
      //   this.isSubmitting = false;
      //   this.resetActiveButtonState();
      //   this.render();
      //   this.setPaymentError('success-url and cancel-url attributes are required for payment.');
      //   return;
      // }
      //
      // try {
      //   const response = await requestTransactionIntent(
      //     this.promotion.entity_id,
      //     this.promotion.page_id,
      //     this.selectedItem.item_id,
      //     name,
      //     email,
      //     successUrl,
      //     cancelUrl,
      //     this.appliedCoupon?.code
      //   );
      //
      //   const { url, session_id, user_profile, expires_at } = response.data || response;
      //
      //   if (!url) {
      //     throw new Error('Payment URL not received from server.');
      //   }
      //
      //   this.dispatchCustomEvent('klaim:pay', {
      //     item: this.selectedItem,
      //     formData: this.formValues,
      //     coupon: this.appliedCoupon,
      //     finalPrice,
      //     currency,
      //     amount_minor: finalPriceMinor,
      //     promotion_id: this.promotion.promotion_id,
      //     session_id,
      //     user_profile,
      //     expires_at
      //   });
      //
      //   window.location.href = url;
      // } catch (error) {
      //   console.error('Failed to request transaction intent:', error);
      //   const errorMessage = error instanceof Error ? error.message : 'Failed to initiate payment.';
      //   this.setPaymentError(errorMessage);
      //   this.isSubmitting = false;
      //   this.resetActiveButtonState();
      //   this.render();
      // }
      // === END COMMENTED OUT ===

      // NEW: Redirect to klaim-user-web standalone payment page
      // The standalone page has Credit Card / PayPal tabs built in
      try {
        const paymentPageBase = this.getAttribute('payment-page-url') || process.env.NEXT_PUBLIC_PAYMENT_PAGE_URL || '';
        if (!paymentPageBase) {
          throw new Error('payment-page-url attribute or NEXT_PUBLIC_PAYMENT_PAGE_URL is required for payment.');
        }

        const params = new URLSearchParams();
        params.set('item_id', this.selectedItem.item_id);
        params.set('name', name);
        params.set('email', email);
        if (this.appliedCoupon?.code) {
          params.set('coupon_code', this.appliedCoupon.code);
        }
        if (successUrl) {
          params.set('success_url', successUrl);
        }
        if (cancelUrl) {
          params.set('cancel_url', cancelUrl);
        }

        // Include split test context if available (as meta_ params for transaction tracking)
        if (this.splitTestId && this.splitTestVariantId) {
          params.set('meta_splitTestId', this.splitTestId);
          params.set('meta_splitTestVariantId', this.splitTestVariantId);
        }

        const entityId = this.promotion.entity_id;
        const pageId = this.promotion.page_id;

        // Dispatch event before redirect
        this.dispatchCustomEvent('klaim:pay', {
          item: this.selectedItem,
          formData: this.formValues,
          coupon: this.appliedCoupon,
          finalPrice,
          currency,
          amount_minor: finalPriceMinor,
          promotion_id: this.promotion.promotion_id,
        });

        // Build payment page URL
        // Localhost: use paymentPageBase as-is with entity_id param
        // Non-localhost: use entity_id as subdomain (e.g. https://{entity_id}.klaim.me/{page_id})
        let paymentUrl: string;
        const isLocalhost = paymentPageBase.includes('localhost') || paymentPageBase.includes('127.0.0.1');
        if (isLocalhost) {
          paymentUrl = `${paymentPageBase}/${pageId}?entity_id=${encodeURIComponent(entityId)}&${params.toString()}`;
        } else {
          const baseUrl = new URL(paymentPageBase);
          const subdomainBase = `${baseUrl.protocol}//${entityId}.${baseUrl.host}`;
          paymentUrl = `${subdomainBase}/${pageId}?${params.toString()}`;
        }
        window.open(paymentUrl, '_blank');
      } catch (error) {
        console.error('Failed to redirect to payment page:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to initiate payment.';
        this.setPaymentError(errorMessage);
        this.isSubmitting = false;
        this.resetActiveButtonState();
        this.render();
      }
    } else {
      // No payment required: call submitSignup (user-transaction POST) and redirect to success_url
      if (!successUrl) {
        this.isSubmitting = false;
        this.resetActiveButtonState();
        this.render();
        this.setPaymentError('success-url attribute is required.');
        return;
      }

      try {
        // Prepare metadata including split test context if available
        const metadata: Record<string, string> = {};
        if (this.splitTestId && this.splitTestVariantId) {
          metadata.split_test_id = this.splitTestId;
          metadata.split_test_variant_id = this.splitTestVariantId;
        }

        const response = await submitSignup(
          this.promotion.app_id!,
          this.promotion.promotion_id || '',
          this.selectedItem.item_id,
          name,
          email,
          this.appliedCoupon?.code,
          undefined, // paymentInfo
          Object.keys(metadata).length > 0 ? metadata : undefined,
          this.splitTestId && this.splitTestVariantId
            ? { split_test_id: this.splitTestId, split_test_variant_id: this.splitTestVariantId }
            : undefined
        );

        // Dispatch event before redirect
        this.dispatchCustomEvent('klaim:claim', {
          item: this.selectedItem,
          formData: this.formValues,
          promotion_id: this.promotion.promotion_id,
          currency,
          amount_minor: finalPriceMinor,
        });

        // Redirect to success URL
        window.location.href = successUrl;
      } catch (error) {
        console.error('Failed to submit signup:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to complete signup.';
        this.setPaymentError(errorMessage);
        this.isSubmitting = false;
        this.resetActiveButtonState();
        this.render();
      }
    }
  }

  private setSelectedItem(itemId: string, options?: { suppressRender?: boolean }): void {
    if (!this.promotion) return;
    const item = this.promotion.items?.find((entry) => entry.item_id === itemId) ?? null;
    this.selectedItem = item;
    if (!options?.suppressRender) {
      this.render();
    }
    
    if (item) {
      this.dispatchCustomEvent('klaim:item-selected', {
        item: item,
        promotion_id: this.promotion.promotion_id
      });
    }
  }

  private async handleApplyCoupon(): Promise<void> {
    if (!this.promotion || !this.promotion.promotion_id) return;
    
    // Guard draft mode
    if (this.isDraftMode) {
      this.showDraftModal();
      return;
    }

    const trimmedCode = this.couponCode.trim();
    if (!trimmedCode) {
      this.couponValidation = {
        isValid: false,
        message: 'Please enter a coupon code.',
        isLoading: false
      };
      this.render();
      return;
    }

    this.dispatchCustomEvent('klaim:coupon-verify-start', {
      code: trimmedCode,
      promotion_id: this.promotion.promotion_id
    });

    this.couponValidation = { isValid: false, message: '', isLoading: true };
    this.render();

    try {
      const verificationResult = await verifyCoupon(this.promotion.promotion_id, trimmedCode);
      
      // API response structure: { message: string, data: { is_valid: boolean, coupon_info: { discount_type, discount_amount, currency } } }
      const data = verificationResult.data;
      const is_valid = data?.is_valid || false;
      
      if (!is_valid || !data) {
        this.couponValidation = {
          isValid: false,
          message: verificationResult.message || 'Invalid coupon code.',
          isLoading: false
        };
        this.appliedCoupon = null;
        this.render();
        return;
      }

      const couponInfo = data.coupon_info;
      if (!couponInfo) {
        this.couponValidation = {
          isValid: false,
          message: 'Invalid coupon response format.',
          isLoading: false
        };
        this.appliedCoupon = null;
        this.render();
        return;
      }

      // Extract coupon info from the API response
      const coupon: CouponCode = {
        code: trimmedCode,
        discount_type: couponInfo.discount_type || 'percentage',
        discount_value: couponInfo.discount_amount || 0,
        min_amount: couponInfo.min_amount,
        max_discount: couponInfo.max_discount,
        promotion_id: this.promotion.promotion_id,
        is_valid: true,
        currency: couponInfo.currency || 'usd'
      };

      // Check the current item against the minimum amount
      const currentItem = this.selectedItem;
      if (currentItem && currentItem.type === 'paid' && typeof currentItem.amount === 'number') {
        const itemPrice = currentItem.amount / 100;
        const minAmount = coupon.min_amount ?? 0;
        if (minAmount > 0 && itemPrice < minAmount) {
          this.couponValidation = {
            isValid: false,
            message: `Minimum order amount is ${this.formatPrice(minAmount)}.`,
            isLoading: false
          };
          this.appliedCoupon = null;
          this.render();
          return;
        }
      }

      this.appliedCoupon = coupon;
      this.couponValidation = {
        isValid: true,
        message: this.buildDiscountMessage(coupon),
        isLoading: false
      };
      this.render();
      
      this.dispatchCustomEvent('klaim:coupon-verify-end', {
        success: true,
        code: trimmedCode,
        coupon,
        promotion_id: this.promotion.promotion_id
      });
      
      // Emit coupon-applied success event
      let finalPrice: number | null = null;
      if (this.selectedItem) {
        const currency = (this.selectedItem as any).currency?.toUpperCase() || coupon.currency?.toUpperCase() || 'USD';
        const finalMinor = this.calculateFinalPriceMinor(this.selectedItem, coupon);
        finalPrice = fromMinor(currency, finalMinor);
      }
      this.dispatchCustomEvent('klaim:coupon-applied', {
        coupon: coupon,
        item: this.selectedItem,
        finalPrice: finalPrice,
        promotion_id: this.promotion.promotion_id
      });
    } catch (error) {
      console.error('Coupon verification failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to verify coupon code.';
      this.couponValidation = {
        isValid: false,
        message: errorMessage,
        isLoading: false
      };
      this.appliedCoupon = null;
      this.render();
      
      this.dispatchCustomEvent('klaim:coupon-verify-end', {
        success: false,
        code: trimmedCode,
        error: error instanceof Error ? error.message : String(error),
        promotion_id: this.promotion.promotion_id
      });
      
      // Emit coupon-verification error event
      this.dispatchCustomEvent('klaim:error', {
        type: 'coupon',
        message: errorMessage,
        couponCode: trimmedCode,
        promotion_id: this.promotion.promotion_id,
        error: error instanceof Error ? error.message : String(error)
      });
    }
  }

  private handleRemoveCoupon(): void {
    const removedCoupon = this.appliedCoupon;
    this.appliedCoupon = null;
    this.couponValidation = { isValid: false, message: '', isLoading: false };
    this.render();
    
    // Emit coupon removal event
    if (removedCoupon && this.promotion && this.selectedItem) {
      const currency = (this.selectedItem as any).currency?.toUpperCase() || removedCoupon.currency?.toUpperCase() || 'USD';
      const finalMinor = this.calculateFinalPriceMinor(this.selectedItem, null);
      const finalPrice = fromMinor(currency, finalMinor);
      this.dispatchCustomEvent('klaim:coupon-removed', {
        coupon: removedCoupon,
        item: this.selectedItem,
        finalPrice: finalPrice,
        promotion_id: this.promotion.promotion_id
      });
    }
  }

  private calculateDiscountedPrice(originalPrice: number, coupon: CouponCode | null): number {
    if (!coupon) {
      return originalPrice;
    }

    const { discount_type, discount_value, min_amount = 0, max_discount, currency } = coupon;
    if (!discount_type || discount_value <= 0 || originalPrice < min_amount) {
      return originalPrice;
    }

    let discountAmount = 0;
    if (discount_type === 'percentage') {
      discountAmount = (originalPrice * discount_value) / 100;
      if (typeof max_discount === 'number') {
        discountAmount = Math.min(discountAmount, max_discount);
      }
    } else {
      // Adjust currency exponent: USD uses cents so divide by 100
      discountAmount = currency === 'usd' ? discount_value / 100 : discount_value;
    }

    // Apply rounding and exponent adjustment
    const finalPrice = originalPrice - discountAmount;
    return Math.max(0, Math.round(finalPrice * 100) / 100);
  }

  private discountMinor(currency: string, baseMinor: number, coupon: CouponCode | null): number {
    if (!coupon || baseMinor <= 0) return 0;

    const { discount_type, discount_value, max_discount, min_amount, currency: ccy } = coupon;
    const cur = (ccy || currency || 'usd').toUpperCase();

    const minMinor = typeof min_amount === 'number' ? toMinorStrict(cur, min_amount) : 0;
    if (minMinor > 0 && baseMinor < minMinor) return 0;

    let dMinor = 0;
    if (discount_type === 'percentage') {
      dMinor = Math.round((baseMinor * discount_value) / 100);
    } else {
      const valueMinor =
        cur === 'USD' ? discount_value : toMinorStrict(cur, discount_value);
      dMinor = valueMinor;
    }

    if (typeof max_discount === 'number') {
      const maxMinor = toMinorStrict(cur, max_discount);
      dMinor = Math.min(dMinor, maxMinor);
    }
    return Math.max(0, Math.min(baseMinor, dMinor));
  }

  private buildDiscountMessage(coupon: CouponCode): string {
    const { discount_type, discount_value, currency } = coupon;
    if (discount_type === 'percentage') {
      return `Coupon applied successfully! ${discount_value}% discount`;
    }

    const amount = currency === 'usd' ? discount_value / 100 : discount_value;
    return `Coupon applied successfully! ${this.formatPrice(amount)} discount`;
  }

  private formatPrice(value: number): string {
    // Return $0.00 if value is invalid, NaN, null/undefined, or zero
    if (value == null || isNaN(value) || !isFinite(value) || value === 0) {
      return '$0.00';
    }
    // Apply rounding/exponent adjustment: clamp to two decimals via toFixed
    const rounded = Math.round(value * 100) / 100;
    return `$${rounded.toFixed(2)}`;
  }

  private calculateFinalPrice(item: Item, coupon: CouponCode | null, originalPriceOverride?: number): number {
    if (item.type === 'free') {
      return 0;
    }
    let originalPrice: number;
    if (originalPriceOverride !== undefined) {
      originalPrice = originalPriceOverride;
    } else {
      const amountCents = item.amount ?? 0;
      originalPrice = amountCents / 100;
      if (this.billingPeriod === 'annual' && item.billing_frequency === BillingFrequency.MonthlyRecurring) {
        originalPrice = originalPrice * 12;
      }
    }
    return this.calculateDiscountedPrice(originalPrice, coupon);
  }

  private calculateFinalPriceMinor(item: Item, coupon: CouponCode | null): number {
    if (item.type === ItemType.Free) return 0;
    
    // Contact type should display original price, not apply discounts
    if (this.isContactItem(item)) {
      const currency = (item as any).currency?.toString().toUpperCase() || 'USD';
      let baseMinor = item.amount ?? 0;
      const isMonthly = item.billing_frequency === BillingFrequency.MonthlyRecurring;
      const months = this.billingPeriod === 'annual' && isMonthly ? 12 : 1;
      return baseMinor * months;
    }

    const currency = (item as any).currency?.toString().toUpperCase()
      || coupon?.currency?.toUpperCase()
      || 'USD';

    let baseMinor = item.amount ?? 0;

    const isMonthly = item.billing_frequency === BillingFrequency.MonthlyRecurring;
    const months = this.billingPeriod === 'annual' && isMonthly ? 12 : 1;
    baseMinor = baseMinor * months;

    const dMinor = this.discountMinor(currency, baseMinor, coupon);
    const finalMinor = Math.max(0, baseMinor - dMinor);
    return finalMinor;
  }

  private toMinorUnits(currency: string, amountFloat: number): number {
    const expMap: Record<string, number> = {
      // Zero-decimal currencies (KRW, JPY, etc.)
      KRW: 0, JPY: 0, VND: 0,
      // Cent-based currencies (USD, EUR, GBP - default 2)
      USD: 2, EUR: 2, GBP: 2, CAD: 2, AUD: 2, CHF: 2, CNY: 2,
      // Three-decimal currencies
      BHD: 3, JOD: 3, KWD: 3, OMR: 3, TND: 3,
    };
    const exp = expMap[currency?.toUpperCase()] ?? 2; // Default to cent-based
    // Round using the 10^exp scale to produce an integer
    const factor = Math.pow(10, exp);
    return Math.max(0, Math.round(amountFloat * factor));
  }

  private get isPreviewMode(): boolean {
    return this.forcedPreviewMode;
  }

  private get isDraftMode(): boolean {
    return this.promotion?.status === 'draft';
  }

  private get isUnclaimed(): boolean {
    return !this.promotion?.app_id;
  }

  private dispatchCustomEvent(eventName: string, detail?: Record<string, unknown>): void {
    const event = new CustomEvent(eventName, {
      detail: detail || {},
      bubbles: true,
      cancelable: true,
      composed: true  // ★ Allow the event to escape the shadow DOM
    });
    this.dispatchEvent(event);
  }

  private formatSeats(value: number): string {
    return value === 1 ? 'seat' : 'seats';
  }

  private applyAlpha(color: string, alphaHex: string): string {
    if (!color.startsWith('#') || color.length !== 7) {
      return color;
    }
    return `${color}${alphaHex}`;
  }

  private setActiveButtonLoadingState(button: HTMLButtonElement): void {
    if (!button) {
      return;
    }
    this.activeSubmitButton = button;
    this.activeSubmitButtonContent = button.innerHTML;
    button.disabled = true;
    button.classList.add('pricing-card__cta--disabled', 'pricing-card__cta--loading');
    button.innerHTML = '<span class="button-spinner" aria-hidden="true"></span> Processing...';
  }

  private resetActiveButtonState(options?: { preserveActiveCard?: boolean }): void {
    if (this.activeSubmitButton) {
      if (this.activeSubmitButtonContent !== null && this.activeSubmitButton.isConnected) {
        this.activeSubmitButton.innerHTML = this.activeSubmitButtonContent;
        this.activeSubmitButton.disabled = false;
        this.activeSubmitButton.classList.remove('pricing-card__cta--disabled', 'pricing-card__cta--loading');
      }
    }
    this.activeSubmitButton = null;
    this.activeSubmitButtonContent = null;
    if (!options?.preserveActiveCard) {
      this.clearActiveCardHighlight();
    }
  }

  private restoreActiveCardHighlight(itemId?: string | null): void {
    if (!itemId || !this.shadowRoot) {
      this.clearActiveCardHighlight();
      return;
    }
    const card = this.shadowRoot.querySelector<HTMLElement>(`.pricing-card[data-item-id="${itemId}"]`);
    if (card) {
      this.setActiveCardHighlight(card);
    } else {
      this.clearActiveCardHighlight();
    }
  }

  private cleanupDetachedActiveElements(): void {
    if (this.activeSubmitButton && !this.activeSubmitButton.isConnected) {
      this.activeSubmitButton = null;
      this.activeSubmitButtonContent = null;
    }
    if (this.activeCardElement && !this.activeCardElement.isConnected) {
      this.activeCardElement = null;
    }
  }

  private setActiveCardHighlight(card: HTMLElement | null): void {
    if (this.activeCardElement && this.activeCardElement !== card && this.activeCardElement.isConnected) {
      this.activeCardElement.classList.remove('pricing-card--active');
    }
    if (card) {
      card.classList.add('pricing-card--active');
      this.activeCardElement = card;
    } else {
      this.activeCardElement = null;
    }
  }

  private clearActiveCardHighlight(): void {
    if (this.activeCardElement && this.activeCardElement.isConnected) {
      this.activeCardElement.classList.remove('pricing-card--active');
    }
    this.activeCardElement = null;
  }

  private attachContactModalListeners(): void {
    if (!this.shadowRoot) return;

    // Close modal on overlay click
    const overlay = this.shadowRoot.querySelector('[data-contact-modal-overlay]');
    if (overlay) {
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          this.closeContactModal();
        }
      });
    }

    // Close modal on close button click
    const closeButton = this.shadowRoot.querySelector('[data-contact-modal-close]');
    if (closeButton) {
      closeButton.addEventListener('click', () => {
        this.closeContactModal();
      });
    }

    // Handle form input changes
    const formFields = this.shadowRoot.querySelectorAll('[data-contact-field]');
    formFields.forEach((field) => {
      field.addEventListener('input', (e) => {
        const target = e.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
        const fieldName = target.getAttribute('data-contact-field');
        if (fieldName) {
          this.contactFormData[fieldName as keyof typeof this.contactFormData] = target.value;
        }
      });
    });

    // Handle form submission
    const form = this.shadowRoot.querySelector('[data-contact-form]') as HTMLFormElement;
    if (form) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        await this.handleContactFormSubmit();
      });
    }
  }

  private closeContactModal(): void {
    if (!this.isContactModalOpen || this.contactModalClosing) {
      return;
    }

    this.contactModalJustOpened = false;
    this.contactModalClosing = true;
    this.render();

    const CLOSE_ANIMATION_DURATION = 450;
    setTimeout(() => {
      this.isContactModalOpen = false;
      this.contactModalClosing = false;
      this.contactSubmitState = 'idle';
      this.render();
      // Clear active card highlight after modal closes with a delay for smooth animation
      setTimeout(() => {
        this.clearActiveCardHighlight();
      }, 100);
    }, CLOSE_ANIMATION_DURATION);
  }

  private showUnclaimedModal(): void {
    this.isUnclaimedModalOpen = true;
    this.unclaimedModalJustOpened = true;
    this.unclaimedModalClosing = false;
    this.render();

    // Reset animation flag after a short delay
    setTimeout(() => {
      this.unclaimedModalJustOpened = false;
    }, 350);

    this.dispatchCustomEvent('klaim:unclaimed-block', {
      message: 'This pricing page is not yet connected to a payment account.',
      promotion_id: this.promotion?.promotion_id
    });
  }

  private closeUnclaimedModal(): void {
    if (!this.isUnclaimedModalOpen || this.unclaimedModalClosing) {
      return;
    }

    this.unclaimedModalJustOpened = false;
    this.unclaimedModalClosing = true;
    this.render();

    const CLOSE_ANIMATION_DURATION = 450;
    setTimeout(() => {
      this.isUnclaimedModalOpen = false;
      this.unclaimedModalClosing = false;
      this.render();
      // Clear active card highlight after modal closes with a delay for smooth animation
      setTimeout(() => {
        this.clearActiveCardHighlight();
      }, 100);
    }, CLOSE_ANIMATION_DURATION);
  }

  private attachUnclaimedModalListeners(): void {
    if (!this.shadowRoot || !this.isUnclaimedModalOpen) return;

    // Close modal on overlay click
    const overlay = this.shadowRoot.querySelector('[data-unclaimed-modal-overlay]');
    if (overlay) {
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          this.closeUnclaimedModal();
        }
      });
    }

    // Close modal on close button click or "Got it" button
    const closeButtons = this.shadowRoot.querySelectorAll('[data-unclaimed-modal-close]');
    closeButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.closeUnclaimedModal();
      });
    });
  }

  private showDraftModal(): void {
    this.isDraftModalOpen = true;
    this.draftModalJustOpened = true;
    this.draftModalClosing = false;
    this.render();

    setTimeout(() => {
      this.draftModalJustOpened = false;
    }, 350);

    this.dispatchCustomEvent('klaim:draft-block', {
      message: 'This promotion is in draft mode and cannot be processed.',
      promotion_id: this.promotion?.promotion_id
    });
  }

  private closeDraftModal(): void {
    if (!this.isDraftModalOpen || this.draftModalClosing) {
      return;
    }

    this.draftModalJustOpened = false;
    this.draftModalClosing = true;
    this.render();

    const CLOSE_ANIMATION_DURATION = 450;
    setTimeout(() => {
      this.isDraftModalOpen = false;
      this.draftModalClosing = false;
      this.render();
      setTimeout(() => {
        this.clearActiveCardHighlight();
      }, 100);
    }, CLOSE_ANIMATION_DURATION);
  }

  private attachDraftModalListeners(): void {
    if (!this.shadowRoot || !this.isDraftModalOpen) return;

    const overlay = this.shadowRoot.querySelector('[data-draft-modal-overlay]');
    if (overlay) {
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          this.closeDraftModal();
        }
      });
    }

    const closeButtons = this.shadowRoot.querySelectorAll('[data-draft-modal-close]');
    closeButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.closeDraftModal();
      });
    });
  }

  private renderDraftModal(): string {
    if (!this.isDraftModalOpen) {
      return '';
    }

    const overlayClasses = [
      'unclaimed-modal-overlay',
      this.draftModalJustOpened ? 'unclaimed-modal-overlay--opening' : '',
      this.draftModalClosing ? 'unclaimed-modal-overlay--closing' : ''
    ].filter(Boolean).join(' ');

    const modalClasses = [
      'unclaimed-modal',
      this.draftModalJustOpened ? 'unclaimed-modal--opening' : '',
      this.draftModalClosing ? 'unclaimed-modal--closing' : ''
    ].filter(Boolean).join(' ');

    return `
      <div class="${overlayClasses}" data-draft-modal-overlay>
        <div class="${modalClasses}">
          <button class="unclaimed-modal-close" data-draft-modal-close aria-label="Close modal">&times;</button>
          <div class="unclaimed-modal-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
            </svg>
          </div>
          <h2 class="unclaimed-modal-title">Draft Mode</h2>
          <p class="unclaimed-modal-text">
            This pricing page is currently in draft mode. Payments are not available yet.
          </p>
          <div class="unclaimed-modal-divider"></div>
          <p class="unclaimed-modal-owner">
            Are you the owner?<br>
            Publish this promotion from your <a href="https://app.getklaim.com" target="_blank" rel="noopener noreferrer">Klaim dashboard</a> to enable payments.
          </p>
          <button class="unclaimed-modal-button" data-draft-modal-close>Got it</button>
        </div>
      </div>
    `;
  }

  private async handleContactFormSubmit(): Promise<void> {
    if (!this.promotion || !this.promotion.promotion_id) return;

    // Validate form
    if (!this.contactFormData.name || 
        !this.contactFormData.email || !this.contactFormData.message) {
      return;
    }

    // Set loading state
    this.contactSubmitState = 'loading';
    // Prevent modal from replaying the opening animation on submit re-render
    this.contactModalJustOpened = false;
    this.render();
    this.attachContactModalListeners();

    try {
      // Send email
      const promotionId = this.promotion.promotion_id || '';
      const name = this.contactFormData.name.trim();
      
      // Ensure spinner is visible for at least 500ms
      const startTime = Date.now();
      const emailPromise = sendContactEmail(
        promotionId,
        name,
        this.contactFormData.email,
        this.contactFormData.message
      );
      
      await emailPromise;
      
      // Wait for minimum loading time
      const elapsed = Date.now() - startTime;
      const minLoadingTime = 500;
      if (elapsed < minLoadingTime) {
        await new Promise(resolve => setTimeout(resolve, minLoadingTime - elapsed));
      }

      // Set success state
      this.contactSubmitState = 'success';
      this.contactModalJustOpened = false;
      this.render();
      this.attachContactModalListeners();

      // Dispatch event
      this.dispatchCustomEvent('klaim:contact-submitted', {
        item: this.selectedItem,
        formData: this.contactFormData,
        promotion_id: this.promotion.promotion_id
      });

      // Close modal after 2 seconds
      setTimeout(() => {
        this.closeContactModal();
      }, 2000);
    } catch (error) {
      console.error('Error submitting contact form:', error);
      // Reset to idle state on error
      this.contactSubmitState = 'idle';
      this.contactModalJustOpened = false;
      this.render();
      this.attachContactModalListeners();
    }
  }

  private syncPreviewOverlay(): void {
    const wrapper = this.shadowRoot?.querySelector('.component-wrapper');
    if (!wrapper) {
      return;
    }
    let overlay = wrapper.querySelector('.preview-overlay');
    if (this.previewMessageVisible) {
      if (!overlay) {
        overlay = document.createElement('div');
        overlay.className = 'preview-overlay';
        overlay.innerHTML = `
          <div class="preview-overlay__backdrop"></div>
          <div class="preview-overlay__message">
            This is a preview — payments are disabled.
          </div>
        `;
        wrapper.appendChild(overlay);
      }
    } else if (overlay) {
      overlay.remove();
    }
    if (!this.previewMessageVisible) {
      this.clearActiveCardHighlight();
    }
  }



  private clearPreviewMessageTimer(): void {
    if (this.previewMessageTimeout !== null) {
      window.clearTimeout(this.previewMessageTimeout);
      this.previewMessageTimeout = null;
    }
  }

  private showPreviewBlockMessage(): void {
    this.clearPreviewMessageTimer();
    this.previewMessageVisible = true;
    this.dispatchCustomEvent('klaim:preview-block', {
      message: 'This is a preview — payments are disabled.',
      promotion_id: this.promotion?.promotion_id
    });
    this.syncPreviewOverlay();

    this.previewMessageTimeout = window.setTimeout(() => {
      this.previewMessageVisible = false;
      this.previewMessageTimeout = null;
      this.syncPreviewOverlay();
    }, PREVIEW_MESSAGE_DURATION_MS);
  }

  private render(): void {
    if (!this.shadowRoot) {
      return;
    }
    const shouldResetActiveState = !this.isSubmitting || this.isPreviewMode;
    if (shouldResetActiveState) {
      this.resetActiveButtonState();
    } else {
      this.cleanupDetachedActiveElements();
    }

    if (this.isLoading) {
      const template = `
        ${this.getStyles()}
        <div class="component-wrapper">
          <div class="loading-message">
            <div class="loading-spinner"></div>
            <p>Loading promotion...</p>
          </div>
        </div>
      `;
      this.shadowRoot.innerHTML = template;
      return;
    }

    if (this.loadError || !this.promotion) {
      const errorMessage = this.loadError || 'Unable to load promotion.';
      const template = `
        ${this.getStyles()}
        <div class="component-wrapper">
          <div class="error-message">
            <span class="error-message__icon">✕</span>
            <span class="error-message__text">${this.escapeAttribute(errorMessage)}</span>
          </div>
        </div>
      `;
      this.shadowRoot.innerHTML = template;
      return;
    }

    // === 커스텀 템플릿 분기 ===
    // custom_template이 있으면 커스텀 렌더링, 없으면 레거시 렌더링
    if (this.promotion.custom_template) {
      this.renderWithTemplate();
      return;
    }

    const colors = this.promotionColor;
    const sharedTextColor = colors.font_color || DEFAULT_TEXT_COLOR;
    const secondaryTextColor = colors.font_color_secondary || sharedTextColor;
    const items = this.promotion.items ?? [];
    const selectedItemId = this.selectedItem?.item_id;
    const isDraft = this.isDraftMode;
    const containerStyle = `
      --color-primary:${colors.primary};
      --color-secondary:${colors.secondary};
      --color-font:${sharedTextColor};
      --color-font-secondary:${secondaryTextColor};
      --color-background:${colors.background_color};
      --primary:${colors.primary};
      --secondary:${colors.secondary};
      --text-primary:${sharedTextColor};
      --text-secondary:${secondaryTextColor};
      --text-light:${sharedTextColor};
      --shared-text:${sharedTextColor};
      --background-primary:${colors.background_color};
      --background-surface:${colors.background_color};
      --accent:${colors.secondary || colors.primary};
      --accent-strong:${this.applyAlpha(colors.primary, 'CC')};
      --success:${colors.primary};
      --danger:${colors.primary};
      --surface-accent:${this.applyAlpha(colors.primary, '12')};
      --surface-accent-light:${this.applyAlpha(colors.primary, '24')};
      --surface-accent-strong:${this.applyAlpha(colors.primary, '40')};
      --surface-accent-border:${this.applyAlpha(colors.primary, '90')};
      --surface-highlight:${this.applyAlpha(colors.primary, '28')};
      --surface-highlight-overlay:${this.applyAlpha(colors.primary, '3A')};
      --credit-pill-bg:${this.applyAlpha(colors.primary, '22')};
      --border-muted:${this.applyAlpha(sharedTextColor, '32')};
      --shadow-soft:0 18px 45px -15px ${this.applyAlpha(colors.primary, '20')};
    `;

    const orderedItems = items.slice();
    const hasEvenItemCount = orderedItems.length > 0 && orderedItems.length % 2 === 0;
    const pricingGridClasses = ['pricing-grid'];
    if (hasEvenItemCount) {
      pricingGridClasses.push('pricing-grid--even');
    }
    const pricingGridClassName = pricingGridClasses.join(' ');
    const itemsHTML = orderedItems
      .map((item, index) => this.renderItemCard(item, index, selectedItemId))
      .join('');

    const couponSectionHTML = ENABLE_COUPON_SECTION ? this.renderCouponSection() : '';
    const hasCouponMessage = ENABLE_COUPON_SECTION && Boolean(this.couponValidation.message);
    const couponMessageHTML = hasCouponMessage
      ? `
        <div class="coupon-message coupon-message--${this.couponValidation.isValid ? 'success' : 'error'}">
          <span class="coupon-message__icon">${this.couponValidation.isValid ? '✓' : '✕'}</span>
          <span class="coupon-message__text">${this.escapeAttribute(this.couponValidation.message)}</span>
        </div>
      `
      : '';

    const couponPanelHTML = ENABLE_COUPON_SECTION && couponSectionHTML
      ? `
        <div class="coupon-panel">
          ${couponSectionHTML}
          ${couponMessageHTML}
        </div>
      `
      : '';

    const paymentErrorHTML = this.paymentError
      ? `
        <div class="payment-error">
          <span class="payment-error__icon">✕</span>
          <span class="payment-error__text">${this.escapeAttribute(this.paymentError)}</span>
        </div>
      `
      : '';

    const draftBannerHTML = '';

    const headingTitle = this.promotion?.title?.trim() || '';
    const headingDescription = this.promotion?.description?.trim() || '';
    const headerHTML = (headingTitle || headingDescription)
      ? `
        <header class="pricing-hero">
          ${headingTitle ? `<h1 class="pricing-hero__title">${this.escapeAttribute(headingTitle)}</h1>` : ''}
          ${headingDescription ? `<p class="pricing-hero__subtitle">${this.escapeAttribute(headingDescription)}</p>` : ''}
        </header>
      `
      : '';

    const billingToggleHTML = this.renderBillingToggle();

    const animateBillingChange = this.pendingBillingAnimation;
    this.pendingBillingAnimation = false;

    const wrapperClasses = [
      'component-wrapper',
      animateBillingChange ? 'component-wrapper--period-change' : ''
    ].filter(Boolean).join(' ');

    const contactModalHTML = this.renderContactModal();
    const unclaimedModalHTML = this.renderUnclaimedModal();
    const draftModalHTML = this.renderDraftModal();

    const template = `
      ${this.getStyles()}
      <div class="${wrapperClasses}" style="${containerStyle}" data-billing-period="${this.billingPeriod}" data-item-count="${items.length}" data-viewport="${this.viewportMode}">
        ${draftBannerHTML}
        ${headerHTML}
        ${billingToggleHTML ? `<div class="pricing-controls">${billingToggleHTML}</div>` : ''}
        ${couponPanelHTML}
        ${paymentErrorHTML}
        <section class="${pricingGridClassName}">
          ${itemsHTML}
        </section>
      </div>
      ${contactModalHTML}
      ${unclaimedModalHTML}
      ${draftModalHTML}
    `;

    this.shadowRoot.innerHTML = template;
    this.syncPreviewOverlay();
    this.attachContactModalListeners();
    this.attachUnclaimedModalListeners();
    this.attachDraftModalListeners();
  }

  private getBillingSuffix(item: Item): string {
    if (item.billing_frequency === BillingFrequency.MonthlyRecurring) {
      return this.billingPeriod === 'annual' ? 'per year' : 'per month';
    }
    if (item.billing_frequency === BillingFrequency.OneTime) {
      return 'one-time';
    }
    if (item.type === 'free') {
      return 'forever';
    }
    return '';
  }

  private getBillingAbbreviation(item: Item): string {
    if (item.billing_frequency === BillingFrequency.MonthlyRecurring) {
      return this.billingPeriod === 'annual' ? 'yr' : 'mo';
    }
    if (item.billing_frequency === BillingFrequency.OneTime) {
      return 'once';
    }
    return item.type === 'free' ? 'life' : 'period';
  }

  private getCreditsChip(item: Item): { label: string; value: string } | null {
    const quantity = item.quantity;
    const unit = item.unit;
    if (typeof quantity !== 'number' || Number.isNaN(quantity) || !unit) {
      return null;
    }
    const baseLabel = this.toTitleCase(unit);
    const formattedQuantity = quantity.toLocaleString('en-US');
    const unitLower = unit.toLowerCase();
    let cadence = '';
    if (item.billing_frequency === BillingFrequency.MonthlyRecurring) {
      cadence = '/ month';
    }
    if (item.billing_frequency === BillingFrequency.OneTime) {
      cadence = '';
    }
    if (item.type === ItemType.Free) {
      cadence = '';
    }
    const cadencePart = cadence ? ` ${cadence}` : '';
    return {
      label: baseLabel,
      value: `${formattedQuantity} ${unitLower}${cadencePart}`
    };
  }

  private getBillingNote(
    item: Item,
    currency: string,
    originalMinor: number,
    months: number,
    isFreeItem: boolean
  ): string {
    // Contact type should not show billing note
    if (this.isContactItem(item)) {
      return '';
    }
    if (isFreeItem) {
      return 'Includes lifetime access';
    }
    if (item.billing_frequency === BillingFrequency.MonthlyRecurring) {
      if (this.billingPeriod === 'annual') {
        const monthlyMinor = months > 0 ? Math.round(originalMinor / months) : originalMinor;
        return `Billed annually · ${formatPriceFromMinor(currency, monthlyMinor)}/mo equivalent`;
      }
      return '';
    }
    if (item.billing_frequency === BillingFrequency.OneTime) {
      return 'One-time payment';
    }
    return '';
  }

  private renderItemCard(item: Item, _index: number, selectedItemId?: string): string {
    const isSelected = item.item_id === selectedItemId;
    const isDisabled = this.isSubmitting;
    const isFocused = Boolean(item.is_focused);
    const focusText = typeof (item as any).focus_text === 'string'
      ? (item as any).focus_text.trim()
      : '';
    const bulletPoints = Array.isArray((item as any).bullet_points)
      ? ((item as any).bullet_points as string[])
      : [];

    const currency = (item as any).currency?.toUpperCase() || this.appliedCoupon?.currency?.toUpperCase() || 'USD';

    let originalMinor = item.amount ?? 0;
    const isMonthly = item.billing_frequency === BillingFrequency.MonthlyRecurring;
    const months = this.billingPeriod === 'annual' && isMonthly ? 12 : 1;
    originalMinor = originalMinor * months;

    const isContact = this.isContactItem(item);
    const finalMinor = this.calculateFinalPriceMinor(item, this.appliedCoupon);
    // Contact type should not show discount information
    const discountMinor = isContact ? 0 : Math.max(0, originalMinor - finalMinor);

    const priceDisplay = finalMinor === 0
      ? 'Free'
      : formatPriceFromMinor(currency, finalMinor);
    const isPriceZero = priceDisplay === '$0.00';
    const originalDisplay = discountMinor > 0
      ? formatPriceFromMinor(currency, originalMinor)
      : null;
    const isOriginalZero = originalDisplay === '$0.00';
    const discountNote = discountMinor > 0
      ? `<span class="pricing-card__discount">Save ${formatPriceFromMinor(currency, discountMinor)}</span>`
      : '';

    const isFreeItem = item.type === ItemType.Free;
    const suffix = this.getBillingSuffix(item);
    const cardTitleMessage = isDisabled ? 'Processing...' : '';
    const actionTitleMessage = cardTitleMessage;
    const safeCardTitle = cardTitleMessage ? this.escapeAttribute(cardTitleMessage) : '';
    const safeButtonTitle = actionTitleMessage ? this.escapeAttribute(actionTitleMessage) : '';

    const creditsChip = this.getCreditsChip(item);
    const billingNote = this.getBillingNote(item, currency, originalMinor, months, isFreeItem);
    const priceMetaHTML = (creditsChip || (billingNote && !isFreeItem))
      ? `
        <div class="pricing-card__price-meta">
          ${creditsChip
            ? `
              <span class="pricing-card__credits">
                <span class="pricing-card__credits-value">${this.escapeAttribute(creditsChip.value)}</span>
              </span>
            `
            : ''}
          ${billingNote && !isFreeItem
            ? `<span class="pricing-card__price-note">${this.escapeAttribute(billingNote)}</span>`
            : ''}
        </div>
      `
      : '';
    const effectiveFeatures = bulletPoints.length > 0 ? bulletPoints : [];

    const cardClasses = [
      'pricing-card',
      isFocused ? 'pricing-card--highlight' : '',
      isSelected ? 'pricing-card--selected' : '',
      isDisabled ? 'pricing-card--disabled' : ''
    ].filter(Boolean).join(' ');

    const featuresHTML = effectiveFeatures
      .map((feature) => `
        <li class="pricing-card__feature">
          <span class="pricing-card__feature-icon" aria-hidden="true">✓</span>
          <span>${this.escapeAttribute(feature)}</span>
        </li>
      `)
      .join('');

    return `
      <div
        class="${cardClasses}"
        data-item-id="${item.item_id}"
        title="${safeCardTitle}"
        role="button"
        tabindex="0"
        aria-pressed="${isSelected ? 'true' : 'false'}"
      >
        <div class="pricing-card__body">
          <header class="pricing-card__header">
            <div class="pricing-card__title-row">
              <h3 class="pricing-card__title">${this.escapeAttribute(item.name)}</h3>
              ${isFocused && focusText
                ? `<span class="pricing-card__focus-pill">${this.escapeAttribute(focusText)}</span>`
                : ''}
            </div>
            ${item.description ? `<p class="pricing-card__subtitle">${this.escapeAttribute(item.description)}</p>` : ''}
          </header>
          <div class="pricing-card__pricing">
            ${originalDisplay && !isFreeItem
              ? `
                <div class="pricing-card__price-original" ${isOriginalZero ? 'style="opacity: 0;"' : ''}>
                  <span>${originalDisplay}</span>
                  <span>/${suffix}</span>
                </div>
              `
              : ''}
            <div class="pricing-card__price-line">
              <span class="pricing-card__price-amount${discountMinor > 0 ? ' pricing-card__price-amount--discounted' : ''}" ${isPriceZero ? 'style="opacity: 0;"' : ''}>
                ${priceDisplay}
              </span>
              ${!isFreeItem ? `<span class="pricing-card__price-period">${suffix}</span>` : ''}
            </div>
            ${!isFreeItem ? discountNote : ''}
            ${priceMetaHTML}
            ${!isFreeItem && item.billing_frequency === 'monthly_recurring' && (this.promotion as any)?.free_trial_enabled && (this.promotion as any)?.free_trial_days
              ? `<div class="pricing-card__trial-badge">${(this.promotion as any).free_trial_days}-day free trial</div>`
              : ''}
          </div>
          <button
            class="pricing-card__cta${isFocused ? ' pricing-card__cta--highlight' : ''}${isDisabled ? ' pricing-card__cta--disabled' : ''}"
            type="button"
            data-action="pay-now"
            data-item-id="${item.item_id}"
            ${isDisabled ? 'disabled' : ''}
            title="${safeButtonTitle || this.escapeAttribute('Get Started')}"
          >
            ${this.isSubmitting
              ? '<span class="button-spinner" aria-hidden="true"></span> Processing...'
              : this.escapeAttribute(this.getButtonLabel(item))}
          </button>
          ${featuresHTML
            ? `
              <ul class="pricing-card__features">
                ${featuresHTML}
              </ul>
            `
            : ''}
        </div>
      </div>
    `;
  }
  private renderBillingToggle(): string {
    if (!FEATURE_FLAGS.enableBillingToggle) {
      return '';
    }
    const isMonthly = this.billingPeriod === 'monthly';
    return `
      <div class="billing-toggle-wrapper">
        <div class="billing-toggle" data-active="${this.billingPeriod}">
          <span class="billing-toggle__slider" aria-hidden="true"></span>
          <button
            type="button"
            class="billing-toggle__button${isMonthly ? ' billing-toggle__button--active' : ''}"
            data-period="monthly"
            aria-pressed="${isMonthly ? 'true' : 'false'}"
          >
            Monthly
          </button>
          <button
            type="button"
            class="billing-toggle__button${!isMonthly ? ' billing-toggle__button--active' : ''}"
            data-period="annual"
            aria-pressed="${!isMonthly ? 'true' : 'false'}"
          >
            Annual
          </button>
        </div>
      </div>
    `;
  }

  private renderInputField(field: UserInfoField): string {
    const value = this.formValues[field.id] ?? '';
    const isDisabled = this.isSubmitting;
    const error = this.fieldErrors?.[field.id] || '';
    const hasError = Boolean(error);
    return `
      <div class="input-field">
        <label class="input-field__label" for="${field.id}">${field.label}</label>
        <input
          class="input-field__input${isDisabled ? ' input-field__input--disabled' : ''}${hasError ? ' input-field__input--error' : ''}"
          type="${field.type}"
          id="${field.id}"
          name="${field.id}"
          placeholder="${field.placeholder}"
          value="${this.escapeAttribute(value)}"
          ${field.required ? 'required' : ''}
          ${isDisabled ? 'disabled' : ''}
        />
        ${hasError ? `<span class="input-field__error">${this.escapeAttribute(error)}</span>` : ''}
      </div>
    `;
  }

  private renderCouponSection(): string {
    if (!ENABLE_COUPON_SECTION) {
      return '';
    }
    const isLoading = this.couponValidation.isLoading;
    const hasAppliedCoupon = Boolean(this.appliedCoupon);
    const isDisabled = this.isSubmitting || isLoading;
    const helperText = 'Apply a coupon code before checkout.';
    const helperTitle = this.escapeAttribute(helperText);
    const inputClasses = ['coupon-input', isDisabled ? 'coupon-input--disabled' : '']
      .filter(Boolean)
      .join(' ');
    const applyButtonDisabled = isDisabled || !this.couponCode.trim();

    return `
      <div class="coupon-field" title="${helperTitle}">
        <span class="coupon-field__title">Have a Coupon Code?</span>
        <input
          class="${inputClasses}"
          type="text"
          id="couponCode"
          name="couponCode"
          placeholder="Enter coupon code"
          value="${this.escapeAttribute(this.couponCode)}"
          ${isDisabled ? 'disabled' : ''}
          title="${helperTitle}"
        />
        ${hasAppliedCoupon
          ? `
            <button
              type="button"
              class="coupon-action coupon-action--remove"
              data-action="remove-coupon"
              ${this.isSubmitting ? 'disabled' : ''}
              title="Remove coupon"
            >
              ✕
            </button>
          `
          : `
            <button
              type="button"
              class="coupon-action"
              data-action="apply-coupon"
              ${applyButtonDisabled ? 'disabled' : ''}
              title="${helperTitle}"
            >
              ${isLoading ? '<span class="spinner" aria-hidden="true"></span>' : 'Apply'}
            </button>
          `}
      </div>
    `;
  }

  private isContactItem(item: Item): boolean {
    return item.type === ItemType.Contact || (item.type as string) === 'contact';
  }

  private getButtonLabel(item: Item): string {
    if (this.isContactItem(item)) {
      return item.contact_text?.trim() || 'Contact us';
    }

    // Show "Start Free Trial" for trial-enabled subscription items
    if (
      item.billing_frequency === 'monthly_recurring' &&
      (this.promotion as any)?.free_trial_enabled &&
      (this.promotion as any)?.free_trial_days
    ) {
      return 'Start Free Trial';
    }

    return 'Get Started';
  }

  private renderUnclaimedModal(): string {
    if (!this.isUnclaimedModalOpen) {
      return '';
    }

    const overlayClasses = [
      'unclaimed-modal-overlay',
      this.unclaimedModalJustOpened ? 'unclaimed-modal-overlay--opening' : '',
      this.unclaimedModalClosing ? 'unclaimed-modal-overlay--closing' : ''
    ].filter(Boolean).join(' ');

    const modalClasses = [
      'unclaimed-modal',
      this.unclaimedModalJustOpened ? 'unclaimed-modal--opening' : '',
      this.unclaimedModalClosing ? 'unclaimed-modal--closing' : ''
    ].filter(Boolean).join(' ');

    return `
      <div class="${overlayClasses}" data-unclaimed-modal-overlay>
        <div class="${modalClasses}">
          <button class="unclaimed-modal-close" data-unclaimed-modal-close aria-label="Close modal">&times;</button>
          <div class="unclaimed-modal-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
            </svg>
          </div>
          <h2 class="unclaimed-modal-title">Payments Not Yet Enabled</h2>
          <p class="unclaimed-modal-text">
            This pricing page hasn't been linked to a payment account yet.
          </p>
          <div class="unclaimed-modal-divider"></div>
          <p class="unclaimed-modal-owner">
            Are you the owner?<br>
            <a href="https://getklaim.com" target="_blank" rel="noopener noreferrer">Visit getklaim.com</a> to connect
          </p>
          <button class="unclaimed-modal-button" data-unclaimed-modal-close>Got it</button>
        </div>
      </div>
    `;
  }

  private renderContactModal(): string {
    if (!this.isContactModalOpen) {
      return '';
    }

    const overlayClasses = [
      'contact-modal-overlay',
      this.contactModalJustOpened ? 'contact-modal-overlay--opening' : '',
      this.contactModalClosing ? 'contact-modal-overlay--closing' : ''
    ].filter(Boolean).join(' ');

    const modalClasses = [
      'contact-modal',
      this.contactModalJustOpened ? 'contact-modal--opening' : '',
      this.contactModalClosing ? 'contact-modal--closing' : ''
    ].filter(Boolean).join(' ');

    const submitButtonContent = 
      this.contactSubmitState === 'loading' 
        ? '<span class="contact-submit-spinner"></span>'
        : this.contactSubmitState === 'success'
        ? '<span class="contact-submit-checkmark">✓</span>'
        : 'Submit';

    return `
      <div class="${overlayClasses}" data-contact-modal-overlay>
        <div class="${modalClasses}">
          <button class="contact-modal-close" data-contact-modal-close aria-label="Close modal">×</button>
          <h2 class="contact-modal-title">Get in Touch</h2>
          <p class="contact-modal-subtitle">You can reach us anytime</p>
          
          <form class="contact-form" data-contact-form>
            <div class="contact-form-group">
              <label for="contact-name">Name</label>
              <input 
                type="text" 
                id="contact-name" 
                name="name" 
                placeholder="John Doe"
                value="${this.escapeAttribute(this.contactFormData.name)}"
                data-contact-field="name"
                required
              />
            </div>
            
            <div class="contact-form-group">
              <label for="contact-email">Email</label>
              <input 
                type="email" 
                id="contact-email" 
                name="email" 
                placeholder="john.doe@example.com"
                value="${this.escapeAttribute(this.contactFormData.email)}"
                data-contact-field="email"
                required
              />
            </div>
            
            <div class="contact-form-group contact-form-group--message">
              <label for="contact-message">Message</label>
              <textarea 
                id="contact-message" 
                name="message" 
                placeholder="How can we help?"
                data-contact-field="message"
                required
              >${this.escapeAttribute(this.contactFormData.message)}</textarea>
            </div>
            
            <button 
              type="submit" 
              class="contact-submit-button"
              data-contact-submit
              ${this.contactSubmitState === 'loading' || this.contactSubmitState === 'success' ? 'disabled' : ''}
            >
              ${submitButtonContent}
            </button>
          </form>
        </div>
      </div>
    `;
  }

  private buildDiscountLabel(coupon: CouponCode): string {
    if (coupon.discount_type === 'percentage') {
      return `${coupon.discount_value}% discount`;
    }

    const amount = coupon.currency === 'usd' ? coupon.discount_value / 100 : coupon.discount_value;
    return `${this.formatPrice(amount)} discount`;
  }

  private escapeAttribute(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // ============================================================
  // Custom Template Rendering (Phase 4)
  // ============================================================

  /**
   * 커스텀 템플릿으로 렌더링
   * - custom_template이 있을 때만 호출됨
   * - 없으면 기존 render() 메서드 사용
   */
  private renderWithTemplate(): void {
    if (!this.shadowRoot || !this.promotion || !this.promotion.custom_template) {
      return;
    }

    const wrapperTemplate = DEFAULT_WRAPPER_TEMPLATE;
    const cardTemplate = this.promotion.custom_template || DEFAULT_CARD_TEMPLATE;

    // 각 아이템을 카드로 렌더링
    const cardsHtml = this.promotion.items?.map((item, index) => {
      return this.renderCardFromTemplate(cardTemplate, item, index);
    }).join('') || '';

    // Wrapper에 카드 삽입
    const finalHtml = wrapperTemplate.replace('{{cards}}', cardsHtml);

    const contactModalHTML = this.renderContactModal();
    const unclaimedModalHTML = this.renderUnclaimedModal();
    const draftModalHTML = this.renderDraftModal();

    this.shadowRoot.innerHTML = this.getStyles() + finalHtml + contactModalHTML + unclaimedModalHTML + draftModalHTML;

    this.attachCustomTemplateListeners();
    this.attachContactModalListeners();
    this.attachUnclaimedModalListeners();
    this.attachDraftModalListeners();

    // 중복 결제 방지: isSubmitting 상태에 따른 버튼 disabled 및 로딩 UI (레거시 렌더링과 동일)
    this.updateCustomTemplateButtonState();
  }

  /**
   * 템플릿 플레이스홀더를 실제 데이터로 치환
   * XSS 방지를 위해 모든 사용자 입력값 이스케이프
   */
  private renderCardFromTemplate(template: string, item: Item, index: number): string {
    const colors = this.promotionColor;
    const currency = (item as any).currency?.toUpperCase() || 'USD';
    const priceMinor = item.amount ?? 0;
    const priceFormatted = this.formatPriceForTemplate(currency, priceMinor);
    const priceRaw = String(fromMinor(currency, priceMinor));

    // 결제 주기 텍스트
    let billingPeriod = '';
    if (item.billing_frequency === BillingFrequency.MonthlyRecurring) {
      billingPeriod = '/month';
    } else if (item.billing_frequency === BillingFrequency.OneTime) {
      billingPeriod = ' one-time';
    } else if (item.type === ItemType.Free) {
      billingPeriod = ' free';
    }

    // Bullet points HTML (XSS 이스케이프 적용)
    const bulletPointsHtml = Array.isArray((item as any).bullet_points)
      ? ((item as any).bullet_points as string[])
          .map(bp => `<li style="margin-bottom: 8px; color: #666;">✓ ${this.escapeAttribute(bp)}</li>`)
          .join('')
      : '';

    // Focus badge 처리
    // is_focused=true: 텍스트 치환, is_focused=false: 전체 span 제거
    const focusBadgeText = item.is_focused
      ? this.escapeAttribute((item as any).focus_text || 'Popular')
      : '';

    // is_focused=false면 {{focus_badge}}를 포함하는 전체 span 제거
    let processedTemplate = template;
    if (!focusBadgeText) {
      processedTemplate = processedTemplate.replace(/<span[^>]*>\s*\{\{focus_badge\}\}\s*<\/span>/gi, '');
    }

    // 14개 플레이스홀더 치환
    return processedTemplate
      // 기본 플레이스홀더
      .replace(/\{\{index\}\}/g, String(index))
      .replace(/\{\{item_id\}\}/g, this.escapeAttribute(item.item_id))
      .replace(/\{\{name\}\}/g, this.escapeAttribute(item.name))
      .replace(/\{\{description\}\}/g, this.escapeAttribute(item.description || ''))
      .replace(/\{\{price\}\}/g, priceFormatted)
      .replace(/\{\{price_raw\}\}/g, priceRaw)
      .replace(/\{\{currency\}\}/g, currency)
      .replace(/\{\{billing_period\}\}/g, billingPeriod)
      .replace(/\{\{billing_text\}\}/g, billingPeriod)  // 호환성: billing_text도 지원
      .replace(/\{\{quantity\}\}/g, String(item.quantity ?? 0))
      .replace(/\{\{unit\}\}/g, this.escapeAttribute(item.unit || ''))
      .replace(/\{\{cta_text\}\}/g, this.escapeAttribute(this.getButtonLabel(item)))
      // HTML 블록 플레이스홀더
      .replace(/\{\{bullet_points\}\}/g, bulletPointsHtml)
      .replace(/\{\{focus_badge\}\}/g, focusBadgeText)
      // 색상 플레이스홀더
      .replace(/\{\{color_primary\}\}/g, this.escapeAttribute(colors.primary))
      .replace(/\{\{color_secondary\}\}/g, this.escapeAttribute(colors.secondary))
      .replace(/\{\{color_background\}\}/g, this.escapeAttribute(colors.background_color))
      .replace(/\{\{color_font\}\}/g, this.escapeAttribute(colors.font_color));
  }

  /**
   * 템플릿용 가격 포맷
   */
  private formatPriceForTemplate(currency: string, minor: number): string {
    if (minor === 0) {
      return 'Free';
    }
    return formatPriceFromMinor(currency, minor);
  }

  /**
   * 커스텀 템플릿 이벤트 리스너 바인딩
   * data-action="cta" 버튼 클릭 → 결제/가입 플로우 트리거
   */
  private attachCustomTemplateListeners(): void {
    if (!this.shadowRoot) return;

    const ctaButtons = this.shadowRoot.querySelectorAll('[data-action="cta"]');
    ctaButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        // 중복 클릭 방지 (레거시 렌더링과 동일)
        if (this.isSubmitting) {
          return;
        }

        const itemId = (e.currentTarget as HTMLElement).getAttribute('data-item-id');
        if (itemId) {
          const item = this.promotion?.items?.find(i => i.item_id === itemId);
          if (item) {
            this.handleItemSelect(item);
          }
        }
      });
    });
  }

  /**
   * 커스텀 템플릿 버튼 상태 업데이트 (중복 결제 방지)
   * 레거시 렌더링과 동일하게 isSubmitting 시 버튼 disabled + 로딩 텍스트 표시
   */
  private updateCustomTemplateButtonState(): void {
    if (!this.shadowRoot) return;

    const isDisabled = this.isSubmitting;
    const ctaButtons = this.shadowRoot.querySelectorAll('[data-action="cta"]');

    ctaButtons.forEach(button => {
      const btn = button as HTMLButtonElement;
      const itemId = btn.getAttribute('data-item-id');
      const item = this.promotion?.items?.find(i => i.item_id === itemId);

      // disabled 상태 설정
      btn.disabled = isDisabled;

      // 로딩 UI 표시 - 클릭된 버튼만
      const isActiveItem = itemId === this.selectedItem?.item_id;
      if (this.isSubmitting && isActiveItem) {
        // 원본 텍스트 저장 (처음 한 번만)
        if (!btn.hasAttribute('data-original-text')) {
          btn.setAttribute('data-original-text', btn.textContent || '');
        }
        btn.innerHTML = '<span class="button-spinner" aria-hidden="true"></span> Processing...';
        btn.style.opacity = '0.7';
        btn.style.cursor = 'not-allowed';
      } else if (!this.isSubmitting) {
        // 원본 텍스트 복원
        const originalText = btn.getAttribute('data-original-text');
        if (originalText) {
          btn.textContent = originalText;
          btn.removeAttribute('data-original-text');
        } else if (item) {
          btn.textContent = this.getButtonLabel(item);
        }
        btn.style.opacity = '';
        btn.style.cursor = '';
      }
    });
  }

  /**
   * 아이템 선택 처리 (커스텀 템플릿용)
   */
  private handleItemSelect(item: Item): void {
    if (this.isPreviewMode) {
      this.showPreviewBlockMessage();
      return;
    }

    if (this.isUnclaimed) {
      this.showUnclaimedModal();
      return;
    }

    if (this.isDraftMode) {
      this.showDraftModal();
      return;
    }

    this.selectedItem = item;
    this.dispatchCustomEvent('klaim:item-selected', {
      item: item,
      promotion_id: this.promotion?.promotion_id
    });

    if (!this.validateForm()) {
      return;
    }

    this.isSubmitting = true;
    this.render();
    this.processPaymentOrSignup();
  }

  private getStyles(): string {
    return `
      <style>
        :host {
          display: block;
          font-family: 'Roboto', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          --color-primary: ${DEFAULT_PROMOTION_COLOR.primary};
          --color-secondary: ${DEFAULT_PROMOTION_COLOR.secondary};
          --color-font: ${DEFAULT_TEXT_COLOR};
          --color-background: ${DEFAULT_PROMOTION_COLOR.background_color};
          --shared-text: var(--color-font);
          color: var(--color-font);
        }

        h1, h2, h3, h4, h5, h6 {
          font-weight: inherit;
        }

        .component-wrapper {
          min-height: 100vh;
          width: 100%;
          box-sizing: border-box;
          background: var(--background-primary, var(--color-background));
          color: var(--shared-text, ${DEFAULT_TEXT_COLOR});
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 5rem 1.5rem 4rem;
          gap: 3rem;
        }

        .component-wrapper[data-viewport="mobile"] {
          max-width: 420px;
          padding: 3rem 1.25rem 2.75rem;
          gap: 2rem;
        }

        .component-wrapper--period-change .pricing-card {
          animation: card-fade 0.45s ease forwards;
        }

        .component-wrapper--period-change .pricing-card:nth-child(2) {
          animation-delay: 0.08s;
        }

        .component-wrapper--period-change .pricing-card:nth-child(3) {
          animation-delay: 0.16s;
        }

        .pricing-hero {
          text-align: center;
          max-width: 720px;
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          margin: 0 auto;
        }

        .pricing-hero__title {
          margin: 0;
          font-size: clamp(2.75rem, 3vw + 1rem, 3.75rem);
          font-weight: 700;
          line-height: 1.1;
          color: var(--shared-text, ${DEFAULT_TEXT_COLOR});
        }

        .pricing-hero__subtitle {
          margin: 0 auto;
          font-size: 1.1rem;
          line-height: 1.7;
          color: var(--shared-text, ${DEFAULT_TEXT_COLOR});
          max-width: 42rem;
          white-space: pre-line;
        }

        .pricing-controls {
          width: 100%;
          display: flex;
          justify-content: center;
        }

        .billing-toggle-wrapper {
          display: flex;
          justify-content: center;
          width: 100%;
        }

        .billing-toggle {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: space-between;
          background: rgba(255, 255, 255, 0.06);
          border-radius: 999px;
          padding: 0.4rem;
          border: 1px solid rgba(255, 255, 255, 0.08);
          min-width: 260px;
          max-width: 320px;
          width: 100%;
          overflow: hidden;
        }

        .billing-toggle__slider {
          position: absolute;
          top: 0.35rem;
          bottom: 0.35rem;
          left: 0.4rem;
          width: calc((100% - 0.8rem) / 2);
          border-radius: 999px;
          background: var(--color-primary);
          transform: translateX(0);
          transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .billing-toggle[data-active="annual"] .billing-toggle__slider {
          transform: translateX(100%);
        }

        .billing-toggle__button {
          position: relative;
          z-index: 1;
          flex: 1;
          border: none;
          background: none;
          color: inherit;
          padding: 0.6rem 1rem;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
          transition: color 0.2s ease;
        }

        .billing-toggle__button--active {
          color: var(--color-background);
        }

        .billing-toggle__button[disabled] {
          cursor: not-allowed;
          opacity: 0.6;
        }

        .coupon-panel {
          width: 100%;
          max-width: 420px;
          margin: 0 auto;
          padding: 1.25rem 1.5rem;
          border-radius: 1.25rem;
          background: rgba(255, 255, 255, 0.08);
          border: 1px solid var(--surface-accent-strong, rgba(255, 255, 255, 0.2));
          box-shadow: var(--shadow-soft, 0 18px 45px -15px rgba(15, 23, 42, 0.28));
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .coupon-field {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .coupon-field__title {
          font-size: 0.85rem;
          font-weight: 600;
          letter-spacing: 0.02em;
          color: inherit;
        }

        .coupon-input {
          width: 100%;
          border-radius: 0.65rem;
          border: 1px solid rgba(255, 255, 255, 0.24);
          background: rgba(0, 0, 0, 0.15);
          padding: 0.85rem 1rem;
          font-size: clamp(0.85rem, 0.3vw + 0.8rem, 1rem);
          color: inherit;
        }

        .coupon-input--disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .coupon-action {
          align-self: flex-start;
          border: none;
          border-radius: 0.65rem;
          padding: 0.65rem 1.25rem;
          background: var(--color-primary);
          color: var(--color-background);
          font-weight: 600;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
        }

        .coupon-action[disabled] {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .coupon-action--remove {
          background: transparent;
          border: 1px solid rgba(255, 255, 255, 0.3);
          color: inherit;
        }

        .coupon-message {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.85rem;
          font-weight: 600;
          border-radius: 0.65rem;
          padding: 0.75rem 1rem;
        }

        .coupon-message--error {
          background: rgba(255, 77, 77, 0.15);
          color: #ffd6d6;
        }

        .coupon-message--success {
          background: rgba(34, 197, 94, 0.2);
          color: #d1fae5;
        }

        .payment-error,
        .draft-banner {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.9rem 1.2rem;
          border-radius: 0.85rem;
          border: 1px solid var(--surface-accent-strong, rgba(255,255,255,0.12));
          background: var(--surface-accent, rgba(255,255,255,0.05));
          font-size: 0.9rem;
          font-weight: 600;
          color: var(--shared-text, ${DEFAULT_TEXT_COLOR});
          margin: 0 auto;
        }

        .payment-error__icon,
        .draft-banner__icon,
        .coupon-message__icon {
          font-size: 1rem;
        }

        .preview-overlay {
          position: fixed;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          pointer-events: none;
          z-index: 999;
        }

        .preview-overlay__backdrop {
          position: absolute;
          inset: 0;
          background: rgba(17, 25, 44, 0.35);
          backdrop-filter: blur(2px);
          opacity: 0;
          animation: previewOverlayBackdrop 2.6s ease forwards;
        }

        .preview-overlay__message {
          position: relative;
          padding: 1.5rem 2.25rem;
          border-radius: 1.1rem;
          background: rgba(255, 255, 255, 0.98);
          color: #0f172a;
          font-size: 1rem;
          font-weight: 600;
          letter-spacing: 0.01em;
          box-shadow: 0 25px 60px -25px rgba(15, 23, 42, 0.5);
          opacity: 0;
          transform: translateY(16px) scale(0.96);
          animation: previewOverlayMessage 2.6s ease forwards;
          text-align: center;
          max-width: min(420px, 90%);
          line-height: 1.5;
        }

        .pricing-grid {
          width: 100%;
          max-width: 1200px;
          --pricing-card-min: 210px;
          --pricing-card-max: 340px;
          --pricing-card-ideal: clamp(var(--pricing-card-min), 28vw, var(--pricing-card-max));
          --pricing-grid-gap: clamp(1.75rem, 3.4vw, 2.75rem);
          display: flex;
          flex-wrap: nowrap;
          justify-content: center;
          align-items: stretch;
          gap: var(--pricing-grid-gap);
          overflow-x: auto;
          overflow-y: visible;
          padding: 2.25rem 0 0.5rem;
          scrollbar-width: thin;
        }

        .pricing-grid::-webkit-scrollbar {
          height: 6px;
        }

        .pricing-grid::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.25);
          border-radius: 999px;
        }

        .pricing-grid::-webkit-scrollbar-track {
          background: transparent;
        }

        .pricing-grid--even {
          width: 100%;
        }

        @media (max-width: 1100px) {
          .pricing-grid--even {
            flex-wrap: wrap;
            overflow-x: hidden;
            overflow-y: visible;
          }

          .pricing-grid--even .pricing-card {
            flex: 1 1 calc((100% - var(--pricing-grid-gap)) / 2);
            max-width: calc((100% - var(--pricing-grid-gap)) / 2);
            min-width: calc((100% - var(--pricing-grid-gap)) / 2);
          }
        }

        .pricing-card--highlight {
          background: var(--surface-highlight, rgba(255, 255, 255, 0.14));
          transform: translateY(-14px);
          border-color: var(--surface-accent-border, rgba(255, 255, 255, 0.34));
        }

        .pricing-card--highlight::before {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: inherit;
          background: var(--surface-highlight-overlay, rgba(255, 255, 255, 0.22));
          pointer-events: none;
          z-index: 0;
        }

        .pricing-card--highlight .pricing-card__cta {
          background: var(--color-primary);
          color: var(--text-secondary, var(--color-font-secondary, var(--color-background)));
        }

        .pricing-card {
          position: relative;
          flex: 1 1 var(--pricing-card-ideal);
          min-width: var(--pricing-card-min);
          max-width: var(--pricing-card-max);
          box-sizing: border-box;
          border-radius: 1.1rem;
          border: 1px solid var(--surface-accent-strong, rgba(255, 255, 255, 0.2));
          background: var(--surface-accent, rgba(255, 255, 255, 0.08));
          backdrop-filter: blur(12px);
          padding: 2.25rem 2rem;
          transition: transform 0.6s ease-out, box-shadow 0.6s ease-out, border-color 0.6s ease-out;
          cursor: pointer;
          display: flex;
          overflow: visible;
          color: inherit;
          box-shadow: var(--shadow-soft, 0 18px 45px -15px rgba(15, 23, 42, 0.28));
        }

        .pricing-card--disabled {
          cursor: not-allowed;
          opacity: 0.55;
        }

        .pricing-card:not(.pricing-card--disabled):hover,
        .pricing-card--active:not(.pricing-card--disabled) {
          transform: translateY(-12px);
          box-shadow: 0 25px 55px -20px rgba(15, 23, 42, 0.35);
        }

        .pricing-card--highlight.pricing-card--active:not(.pricing-card--disabled) {
          transform: translateY(-14px);
        }

        .pricing-card__body {
          display: flex;
          flex-direction: column;
          width: 100%;
          gap: 1.5rem;
          position: relative;
          z-index: 1;
        }

        .pricing-card__header {
          display: flex;
          flex-direction: column;
          gap: 0.6rem;
        }

        .pricing-card__title-row {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .pricing-card__title {
          margin: 0;
          font-size: 1.55rem;
          font-weight: 700;
        }

        .pricing-card__focus-pill {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.25rem 0.75rem;
          border-radius: 999px;
          background: var(--color-secondary, var(--color-primary));
          background: color-mix(in srgb, var(--color-background) 35%);
          color: var(--color-primary);
          border: 1px solid var(--color-primary);
          font-size: 0.8rem;
          font-weight: 600;
          letter-spacing: 0.01em;
        }

        .pricing-card__trial-badge {
          display: inline-block;
          margin-top: 0.5rem;
          padding: 0.25rem 0.75rem;
          border-radius: 999px;
          background: #2DD881;
          color: #140D4F;
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 0.01em;
        }

        .pricing-card__subtitle {
          margin: 0;
          font-size: 0.95rem;
          line-height: 1.5;
          color: inherit;
          opacity: 0.85;
        }

        .pricing-card__pricing {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .pricing-card__price-original {
          display: inline-flex;
          gap: 0.25rem;
          font-size: 0.9rem;
          opacity: 0.7;
          text-decoration: line-through;
        }

        .pricing-card__price-line {
          display: flex;
          align-items: baseline;
          gap: 0.35rem;
        }

        .pricing-card__price-amount {
          font-size: 2.6rem;
          font-weight: 700;
          line-height: 1;
        }

        .pricing-card__price-amount--discounted {
          opacity: 0.95;
        }

        .pricing-card__price-period {
          font-size: 1rem;
          font-weight: 400;
          opacity: 0.75;
        }

        .pricing-card__discount {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          padding: 0.35rem 0.75rem;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.12);
          font-size: 0.8rem;
          font-weight: 600;
          width: fit-content;
        }

        .pricing-card__price-meta {
          margin-top: 0.55rem;
          margin-bottom: 0.75rem;
          display: inline-flex;
          gap: 0.5rem;
          align-items: center;
          flex-wrap: wrap;
          color: inherit;
        }

        .pricing-card__credits {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.45rem;
          padding: 0.4rem 0.85rem;
          border-radius: 999px;
          background: var(--color-primary);
          color: var(--text-secondary, var(--color-font-secondary, var(--color-font)));
          font-size: 1rem;
          white-space: nowrap;
          max-width: 100%;
          width: fit-content;
          flex-shrink: 0;
        }

        .pricing-card__credits-label {
          font-weight: 500;
          letter-spacing: 0.01em;
        }

        .pricing-card__credits-value {
          font-weight: 600;
          letter-spacing: 0.01em;
        }

        .pricing-card__price-note {
          font-size: 0.85rem;
          font-weight: 450;
          color: var(--text-secondary, var(--color-font-secondary, var(--color-font)));
        }

        .pricing-card__cta {
          margin-top: 0.9rem;
          padding: 0.9rem 1.25rem;
          border-radius: 0.65rem;
          border: none;
          font-size: 1rem;
          font-weight: 550;
          background: var(--color-primary);
          color: var(--color-background);
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          transition: transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease;
        }

        .pricing-card:not(.pricing-card--highlight) .pricing-card__cta {
          background: var(--color-background);
          color: var(--text-primary, var(--color-font, var(--color-primary)));
          border: 1.5px solid var(--surface-accent-border, var(--color-primary));
        }

        .pricing-card__cta:not(.pricing-card__cta--disabled):hover {
          transform: translateY(-3px);
          box-shadow: 0 18px 30px -20px rgba(15, 23, 42, 0.4);
        }

        .pricing-card__cta--disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .pricing-card__features {
          margin: 0;
          padding: 0;
          list-style: none;
          display: flex;
          flex-direction: column;
          gap: 0.9rem;
          margin-top: 1rem;
        }

        .pricing-card__feature {
          display: flex;
          align-items: flex-start;
          gap: 0.65rem;
          font-size: 0.9rem;
          line-height: 1.5;
          opacity: 0.9;
        }

        .pricing-card__feature-icon {
          display: inline-block;
          margin-top: 0.05rem;
          color: var(--color-secondary, inherit);
          font-size: 0.9rem;
          font-weight: 600;
        }

        .button-spinner {
          width: 1rem;
          height: 1rem;
          border-radius: 50%;
          border: 2px solid currentColor;
          border-top-color: transparent;
          animation: spin 0.75s linear infinite;
        }

        .spinner {
          width: 0.9rem;
          height: 0.9rem;
          border-radius: 50%;
          border: 2px solid currentColor;
          border-top-color: transparent;
          animation: spin 0.75s linear infinite;
        }

        .loading-message {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
          padding: 4rem 2rem;
        }

        .loading-spinner {
          width: 3rem;
          height: 3rem;
          border-radius: 50%;
          border: 4px solid rgba(255, 255, 255, 0.2);
          border-top-color: var(--color-primary);
          animation: spin 0.75s linear infinite;
        }

        .error-message {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 1.5rem;
          border-radius: 1rem;
          background: rgba(255, 77, 77, 0.12);
          border: 1px solid rgba(255, 77, 77, 0.3);
          color: inherit;
          max-width: 600px;
          margin: 0 auto;
        }

        .error-message__icon {
          font-size: 1.2rem;
        }

        @keyframes previewOverlayBackdrop {
          0% {
            opacity: 0;
          }
          18% {
            opacity: 1;
          }
          78% {
            opacity: 1;
          }
          100% {
            opacity: 0;
          }
        }

        @keyframes previewOverlayMessage {
          0% {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          22% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
          78% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
          100% {
            opacity: 0;
            transform: translateY(-10px) scale(0.98);
          }
        }

        @media (max-width: 768px) {
          .component-wrapper {
            padding: 4rem 1.25rem 3rem;
          }

          .pricing-card {
            padding: 2rem 1.5rem;
          }

          .pricing-grid {
            --pricing-card-min: 100%;
            --pricing-card-max: 100%;
            --pricing-card-ideal: 100%;
            flex-direction: column;
            overflow-x: hidden;
          }

          .pricing-grid--even {
            flex-wrap: nowrap;
          }

          .pricing-grid--even .pricing-card {
            min-width: 100%;
            max-width: 100%;
            flex: 1 1 100%;
          }

          .coupon-panel {
            max-width: 100%;
          }
        }

        @keyframes card-fade {
          from {
            opacity: 0;
            transform: translateY(25px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        /* Unclaimed Modal Styles */
        .unclaimed-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          padding: 20px;
        }

        .unclaimed-modal-overlay--opening {
          animation: fadeIn 0.2s ease-out;
        }

        .unclaimed-modal-overlay--closing {
          animation: fadeOut 0.45s ease-in forwards;
        }

        .unclaimed-modal {
          background: white;
          border-radius: 20px;
          padding: 2.5rem 2rem;
          max-width: 400px;
          width: 100%;
          position: relative;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          text-align: center;
        }

        .unclaimed-modal--opening {
          animation: slideUp 0.3s ease-out;
        }

        .unclaimed-modal--closing {
          animation: slideDown 0.45s ease-in forwards;
        }

        .unclaimed-modal-close {
          position: absolute;
          top: 1rem;
          right: 1rem;
          background: none;
          border: none;
          font-size: 1.5rem;
          color: #6b7280;
          cursor: pointer;
          line-height: 1;
          padding: 0;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          transition: background-color 0.2s, color 0.2s;
        }

        .unclaimed-modal-close:hover {
          background-color: #f3f4f6;
          color: #374151;
        }

        .unclaimed-modal-icon {
          color: #9ca3af;
          margin-bottom: 1rem;
        }

        .unclaimed-modal-icon svg {
          width: 48px;
          height: 48px;
        }

        .unclaimed-modal-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: #111827;
          margin: 0 0 0.75rem 0;
        }

        .unclaimed-modal-text {
          font-size: 0.9375rem;
          color: #6b7280;
          margin: 0 0 1.25rem 0;
          line-height: 1.5;
        }

        .unclaimed-modal-divider {
          height: 1px;
          background: #e5e7eb;
          margin: 1.25rem 0;
        }

        .unclaimed-modal-owner {
          font-size: 0.875rem;
          color: #6b7280;
          margin: 0 0 1.5rem 0;
          line-height: 1.6;
        }

        .unclaimed-modal-owner a {
          color: #3b82f6;
          text-decoration: none;
          font-weight: 500;
        }

        .unclaimed-modal-owner a:hover {
          text-decoration: underline;
        }

        .unclaimed-modal-button {
          background: #111827;
          color: white;
          border: none;
          border-radius: 10px;
          padding: 0.75rem 2rem;
          font-size: 0.9375rem;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .unclaimed-modal-button:hover {
          background: #1f2937;
        }

        /* Contact Modal Styles */
        .contact-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          padding: 20px;
        }

        .contact-modal-overlay--opening {
          animation: fadeIn 0.2s ease-out;
        }

        .contact-modal-overlay--closing {
          animation: fadeOut 0.45s ease-in forwards;
        }

        .contact-modal {
          background: white;
          border-radius: 20px;
          padding: 2rem;
          max-width: 500px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          position: relative;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }

        .contact-modal--opening {
          animation: slideUp 0.3s ease-out;
        }

        .contact-modal--closing {
          animation: slideDown 0.45s ease-in forwards;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes fadeOut {
          from {
            opacity: 1;
          }
          to {
            opacity: 0;
          }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideDown {
          from {
            opacity: 1;
            transform: translateY(0);
          }
          to {
            opacity: 0;
            transform: translateY(20px);
          }
        }

        .contact-modal-close {
          position: absolute;
          top: 1rem;
          right: 1rem;
          background: none;
          border: none;
          font-size: 2rem;
          color: #6b7280;
          cursor: pointer;
          line-height: 1;
          padding: 0;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 12px;
          transition: background 0.2s, color 0.2s;
        }

        .contact-modal-close:hover {
          background: #f3f4f6;
          color: #1f2937;
        }

        .contact-modal-title {
          font-size: 2rem;
          font-weight: 700;
          color: #1f2937;
          margin: 0 0 0.5rem 0;
        }

        .contact-modal-subtitle {
          font-size: 1.1rem;
          color: #6b7280;
          margin: 0 0 2.5rem 0;
        }

        .contact-form {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .contact-form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }

        .contact-form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .contact-form-group label {
          font-weight: 500;
          color: #1f2937;
          font-size: 0.9rem;
        }

        .contact-form-group input,
        .contact-form-group textarea,
        .contact-form-group select {
          padding: 0.75rem 1rem;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          font-size: 1rem;
          font-family: inherit;
          transition: border-color 0.2s, box-shadow 0.2s;
          background: white;
          color: #1f2937;
          box-sizing: border-box;
          width: 100%;
        }

        .contact-form-group input:focus,
        .contact-form-group textarea:focus,
        .contact-form-group select:focus {
          outline: none;
          border-color: var(--color-primary);
          box-shadow: 0 0 0 3px color-mix(in srgb, var(--color-primary) 10%, transparent);
        }

        .contact-input-with-icon {
          position: relative;
          width: 100%;
        }

        .contact-input-icon {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: #6b7280;
          font-size: 1.1rem;
        }

        .contact-input-with-icon input {
          padding-left: 2.5rem;
          width: 100%;
          box-sizing: border-box;
        }

        .contact-form-group--message {
          width: 100%;
        }

        .contact-form-group--message textarea {
          min-height: 100px;
          resize: vertical;
          width: 100%;
          box-sizing: border-box;
        }

        .contact-submit-button {
          padding: 0.9rem 1.5rem;
          background: var(--color-primary);
          color: white;
          border: 1px solid transparent;
          border-radius: 12px;
          font-size: 1rem;
          font-family: inherit;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s, transform 0.1s, opacity 0.2s;
          margin-top: 0.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 48px;
          width: 100%;
          box-sizing: border-box;
        }

        .contact-submit-button:hover:not(:disabled) {
          background: var(--color-primary);
          opacity: 0.9;
          transform: translateY(-1px);
        }

        .contact-submit-button:active:not(:disabled) {
          transform: translateY(0);
        }

        .contact-submit-button:disabled {
          cursor: not-allowed;
          opacity: 0.8;
        }

        .contact-submit-spinner {
          width: 20px;
          height: 20px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        .contact-submit-checkmark {
          display: inline-block;
          margin-top: 0.05rem;
          color: var(--color-font-color-secondary);
          font-size: 0.9rem;
          font-weight: 600;
          font-family: inherit;
        }

        .contact-form-disclaimer {
          font-size: 0.75rem;
          color: #9ca3af;
          text-align: center;
          margin: 0.5rem 0 0 0;
        }

        .contact-form-disclaimer strong {
          color: #6b7280;
          font-weight: 600;
        }

        @media (max-width: 640px) {
          .contact-form-row {
            grid-template-columns: 1fr;
          }

          .contact-modal {
            padding: 1.5rem;
            margin: 10px;
          }
        }
      </style>
    `;
  }

}

if (!customElements.get('klaim-pricing-table')) {
  customElements.define('klaim-pricing-table', KlaimPricingTableV2);
}
